import { trackAdActivity } from "../../utils/adAnalytics";
/* eslint-disable */
/* eslint-disable react-hooks/exhaustive-deps */
// src/pages/Cutter.js
// 필요 패키지: npm i three clipper-lib

import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { SVGLoader } from "three/examples/jsm/loaders/SVGLoader.js";
import { CSS2DObject, CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import ClipperLib from "clipper-lib";
import "./Cutter.css";
import { STLExporter } from "three/examples/jsm/exporters/STLExporter.js";
import { useTranslation } from "react-i18next";
import { supabase } from "../../supabase";
import { useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import { useLocation, useHistory } from "react-router-dom";
import { FillVsStrokeDiagram, ClosedVsBrokenDiagram } from "../Guide/GuideMockups";
import { FILE_SAVE_LIMIT } from "../../config/features";
import { countSaved } from "../../utils/storageQuota";
import { saveOrderStl, saveGalleryHandoff } from "../../utils/stlHandoff";
import { ModeIcon, PART_COLORS } from "../../components/ModeIcon";
import { useModal } from "../../context/ModalContext";
import * as BufferGeometryUtils from "three/examples/jsm/utils/BufferGeometryUtils.js";
import {
  preloadHandleHoleGeometries,
  addHandleAndHole,
  buildWeldedStampGeometry,
  buildWeldedCutterGeometry,
  stripSocketOuterShell,
} from "./handleHole";

/* ===== Constants ===== */
const MIN_SIDEBAR = 260;
const MAX_SIDEBAR = 520;
const PX_TO_MM = 0.264583; // 96dpi(px)→mm
const DEFAULT_TARGET_MM = 70; // 치수 미입력 시 기본 스케일(mm)
/* 사용자가 즐겨 쓰는 치수 기본값. 매번 다시 입력하지 않도록 브라우저에 1벌 보관한다.
   도안마다 달라지는 값(scaleMm=전체 크기)은 넣지 않는다 — 도안 크기는 그림마다 다르다. */
const DIMENSION_PRESET_KEY = "cookistory_cutter_dimensions";
const DIMENSION_FIELDS = [
  "knifeH",
  "knifeT",
  "baseH",
  "baseT",
  "stampBaseH",
  "stampTextH",
  "stampFitGap",
];
const DIMENSION_DEFAULTS = {
  knifeH: 20,
  knifeT: 1.5,
  baseH: 5,
  baseT: 3,
  stampBaseH: 7,
  stampTextH: 4,
  stampFitGap: 0.7,
};

function readDimensionPreset() {
  try {
    const raw = JSON.parse(localStorage.getItem(DIMENSION_PRESET_KEY) || "null");
    if (!raw || typeof raw !== "object") return null;
    const out = {};
    for (const k of DIMENSION_FIELDS) {
      const v = Number(raw[k]);
      if (Number.isFinite(v) && v > 0) out[k] = v;
    }
    return Object.keys(out).length ? out : null;
  } catch {
    return null;
  }
}

const CLIPPER_SCALE = 100000;
const STAMP_BASE_EXPAND_MM = 1.5; // 스탬프 베이스를 도안 외곽보다 바깥으로 넓히는 기본 오프셋(mm)
const STAMP_FIT_GAP_MIN_MM = 0.7;
const STAMP_FIT_GAP_MAX_MM = 3;
const FLANGE_MIN_MM = 0.6;
const SCALE_MIN_MM = 30;
const SCALE_MAX_MM = 150;
const MM_PER_IN = 25.4;
const BUILD_PLATE_SIZE_MM = 250;
const GRID_LABEL_STEP_MM = 50;
const CURVE_MIN = 15;
const CURVE_MAX = 200
const CURVE_DEFAULT = 80;
// 도장면 윤곽 재샘플링 목표 간격(mm). 커터 벽(≈1mm)보다 촘촘해 육안상 매끈하면서 과샘플링 제거.
const FACE_CHORD_MM = 0.5;
// STL 내보내기에서 맞닿는 파트를 한 덩어리로 잇는 정점 병합 허용오차(mm)
const WELD_TOLERANCE_MM = 1e-4;
/* 1µm — handleHole.js의 PT_EPS와 같은 기준. 프린터 해상도의 1/100이라 형상엔 영향 없음 */
const SNAP_TOLERANCE_MM = 1e-3;
const FIXED_VIEW_DIR = new THREE.Vector3(0, 3.1, -1).normalize();
const FIXED_VIEW_DISTANCE = 380;

/* ===== 2026-10-15 리뉴얼 화면용 상수·작은 컴포넌트 ===== */
const SIZE_PRESETS = [["small", 50], ["medium", 70], ["large", 90], ["xlarge", 120]];
// 3D 부품 색 — 아래 build* 함수의 재질 색과 같아야 한다
const LEGEND_PARTS = [
  ["cutterWall", "cutterWall", ["both", "cutter"]],
  ["cutterBase", "cutterBase", ["both", "cutter"]],
  ["stampFace", "stampFace", ["both", "stamp"]],
  ["stampBase", "stampBase", ["both", "stamp"]],
];
const EXAMPLES = [["logo", "example-logo.svg"], ["dog", "example-dog.svg"], ["panda", "example-panda.svg"]];

/* 세부 치수 입력칸 하나 — 누르면 3D 화면의 해당 부품이 강조된다 */
function DimField({ label, unitLabel, part, selectedPart, setSelectedPart, range, ...inputProps }) {
  return (
    <label className={`ckc-f ${selectedPart === part ? "hl" : ""}`} onClick={() => setSelectedPart(part)}>
      <small>{label}</small>
      <span className="ckc-v">
        <input type="number" {...inputProps} style={{ width: `${String(inputProps.value ?? "").length + 1.2}ch` }} />
        <span>{unitLabel}</span>
        <em>{range}</em>
      </span>
    </label>
  );
}

export default function CutterPage() {
  /* ===== UI state ===== */
  const [svgText, setSvgText] = useState("");
  const [fileName, setFileName] = useState("");
  const [previewUrl, setPreviewUrl] = useState(null);
  const previewUrlRef = useRef(null);

  const [mode, setMode] = useState("both");
  const [scaleMm, setScaleMm] = useState();
  // 그림 올리기 전엔 scaleMm 이 비어 슬라이더가 30mm 끝에 붙고 숫자칸이 비었다 → 기본값(70)으로 보여주기만 (2026-10-16 김현비)
  const shownScaleMm = scaleMm === undefined || scaleMm === "" ? DEFAULT_TARGET_MM : scaleMm;
  const [unit, setUnit] = useState("mm");

  // 커터/스탬프 파라미터
  const [knifeH, setKnifeH] = useState("");
  const [knifeT, setKnifeT] = useState("");
  const [baseH, setBaseH] = useState("");
  const [baseT, setBaseT] = useState("");
  const [stampBaseH, setStampBaseH] = useState("");
  const [stampTextH, setStampTextH] = useState("");
  const [stampFitGap, setStampFitGap] = useState("");
  const [stampOuterInclude, setStampOuterInclude] = useState(true);
  const handleHoleReadyRef = useRef(false);
  const [handleHoleReady, setHandleHoleReady] = useState(false);
  const plugHeightRef = useRef(6);
  const [scale, setScale] = useState("");
  const [keepDimensionRatio, setKeepDimensionRatio] = useState(false);
  const [dimensionBaseScaleMm, setDimensionBaseScaleMm] = useState(null);

  // 사이드바
  const [sidebarW, setSidebarW] = useState(390);
  const resizerRef = useRef(null);

  // DnD
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  // SVG aspect
  const [svgAspect, setSvgAspect] = useState(null);

  // 곡선 부드러움
  const [curveSegments, setCurveSegments] = useState(CURVE_DEFAULT);

  const [tempCurve, setTempCurve] = useState(CURVE_DEFAULT);

  const curveProgress =
    ((tempCurve - CURVE_MIN) / (CURVE_MAX - CURVE_MIN)) * 100;

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurveSegments(tempCurve);
    }, 200);
    return () => clearTimeout(timer);
  }, [tempCurve]);

  const [isRebuilding, setIsRebuilding] = useState(false);
  const [rebuildLabel, setRebuildLabel] = useState("");

  const [sourceType, setSourceType] = useState("svg");

  // 파라미터 키
  const dimensionScaleRatio = useMemo(() => {
    if (!keepDimensionRatio) return 1;

    const currentScale = Number(scaleMm);
    const baseScale = Number(dimensionBaseScaleMm);

    if (
      !Number.isFinite(currentScale) ||
      currentScale <= 0 ||
      !Number.isFinite(baseScale) ||
      baseScale <= 0
    ) {
      return 1;
    }

    return currentScale / baseScale;
  }, [keepDimensionRatio, scaleMm, dimensionBaseScaleMm]);

  const clampDim = (v, min, max) => {
    if (v === "" || v === null || v === undefined) return "";
    const n = Number(v);
    if (isNaN(n)) return min;
    return Math.min(max, Math.max(min, n));
  };

  const effectiveKnifeH = useMemo(
    () => clampDim(
      keepDimensionRatio ? scaleDimensionValue(knifeH, dimensionScaleRatio) : knifeH,
      1, 100
    ),
    [keepDimensionRatio, knifeH, dimensionScaleRatio]
  );

  const effectiveKnifeT = useMemo(
    () => clampDim(
      keepDimensionRatio ? scaleDimensionValue(knifeT, dimensionScaleRatio) : knifeT,
      1, 10
    ),
    [keepDimensionRatio, knifeT, dimensionScaleRatio]
  );

  const effectiveBaseH = useMemo(
    () => clampDim(
      keepDimensionRatio ? scaleDimensionValue(baseH, dimensionScaleRatio) : baseH,
      1, 100
    ),
    [keepDimensionRatio, baseH, dimensionScaleRatio]
  );

  const effectiveBaseT = useMemo(
    () => clampDim(
      keepDimensionRatio ? scaleDimensionValue(baseT, dimensionScaleRatio) : baseT,
      1, 10
    ),
    [keepDimensionRatio, baseT, dimensionScaleRatio]
  );

  const effectiveStampBaseH = useMemo(
    () => clampDim(
      keepDimensionRatio
        ? scaleDimensionValue(stampBaseH, dimensionScaleRatio)
        : stampBaseH,
      1, 100
    ),
    [keepDimensionRatio, stampBaseH, dimensionScaleRatio]
  );

  const effectiveStampTextH = useMemo(
    () => clampDim(
      keepDimensionRatio
        ? scaleDimensionValue(stampTextH, dimensionScaleRatio)
        : stampTextH,
      1, 100
    ),
    [keepDimensionRatio, stampTextH, dimensionScaleRatio]
  );

  const effectiveStampFitGap = useMemo(
    () => {
      if (stampFitGap === "" || stampFitGap === null || stampFitGap === undefined) return "";
      const v = Number(stampFitGap);
      if (isNaN(v)) return DIMENSION_DEFAULTS.stampFitGap;
      return Math.min(STAMP_FIT_GAP_MAX_MM, Math.max(STAMP_FIT_GAP_MIN_MM, v));
    },
    [stampFitGap]
  );

  const paramsKey = useMemo(
    () =>
      JSON.stringify([
        effectiveKnifeH,
        effectiveKnifeT,
        effectiveBaseH,
        effectiveBaseT,
        effectiveStampBaseH,
        effectiveStampTextH,
        effectiveStampFitGap,
        curveSegments,
        keepDimensionRatio,
        stampOuterInclude,
      ]),
    [
      effectiveKnifeH,
      effectiveKnifeT,
      effectiveBaseH,
      effectiveBaseT,
      effectiveStampBaseH,
      effectiveStampTextH,
      effectiveStampFitGap,
      curveSegments,
      keepDimensionRatio,
      stampOuterInclude,
    ]
  );

  const effectiveCurveSegments = useMemo(() => {
    if (sourceType === "png") {
      if (curveSegments <= 64) {
        return Math.max(28, Math.round(curveSegments * 0.72));
      }
      return Math.max(28, Math.round(46 + (curveSegments - 64) * 0.9));
    }
    return curveSegments;
  }, [sourceType, curveSegments]);

  const cutterCurveSegments = useMemo(() => {
    if (sourceType === "png") {
      return Math.min(260, Math.max(56, effectiveCurveSegments + 20));
    }
    return Math.min(260, Math.max(56, effectiveCurveSegments + 10));
  }, [sourceType, effectiveCurveSegments]);

  // 파트 선택
  const [selectedPart, setSelectedPart] = useState(null);
  // 리뉴얼 화면 상태
  const [modelDims, setModelDims] = useState(null); // { w, d, h } mm
  const [advOpen, setAdvOpen] = useState(false);
  const [viewKind, setViewKind] = useState("iso");
  const [autoRotate, setAutoRotate] = useState(false);
  const [savedFileCount, setSavedFileCount] = useState(null);

  const [downloadMsg, setDownloadMsg] = useState("");

  const [downloading, setDownloading] = useState(false);

  const [isConvertingSvg, setIsConvertingSvg] = useState(false);
  const [svgGuideOpen, setSvgGuideOpen] = useState(false);

  const [storageExceeded, setStorageExceeded] = useState(false);

  // 언어
  const { t } = useTranslation();
  const { showAlert } = useModal();

  // 마이페이지에 보관
  const [saveToMyPage, setSaveToMyPage] = useState(false);

  const prevScaleMmRef = useRef(null);
  const isScaleSyncUpdateRef = useRef(false);

  const mmToIn = (mm) => mm / MM_PER_IN;

  const displayValue = (mmValue, digits = unit === "in" ? 3 : 2) => {
    if (mmValue === "" || mmValue === null || mmValue === undefined) return "";
    const num = Number(mmValue);
    if (!Number.isFinite(num)) return "";
    const converted = unit === "in" ? mmToIn(num) : num;
    return Number(converted.toFixed(digits));
  };

  const toDisplayUnit = (mmValue) => {
    return displayValue(mmValue);
  };

  const fromDisplayUnit = (rawValue) => {
    if (rawValue === "") return "";
    const num = Number(rawValue);
    if (!Number.isFinite(num)) return "";
    const mm = unit === "in" ? num * MM_PER_IN : num;
    return Number(mm.toFixed(3));
  };

  function scaleDimensionValue(value, ratio) {
    if (value === "" || value === null || value === undefined) return value;
    const num = Number(value);
    if (!Number.isFinite(num)) return value;
    return Number((num * ratio).toFixed(3));
  }

  const toBaseDimensionValue = (effectiveValue) => {
    if (!keepDimensionRatio) return effectiveValue;

    const ratio = Number(dimensionScaleRatio);
    if (!Number.isFinite(ratio) || ratio === 0) return effectiveValue;

    return scaleDimensionValue(effectiveValue, 1 / ratio);
  };

  /* 지금 입력된 치수를 내 기본값으로 저장 — 다음에 새 도안을 올리면 이 값으로 시작한다.
     전체 크기(scaleMm)는 도안마다 달라서 제외한다. */
  const [presetMsg, setPresetMsg] = useState("");
  function saveDimensionPreset() {
    const current = {
      knifeH: effectiveKnifeH,
      knifeT: effectiveKnifeT,
      baseH: effectiveBaseH,
      baseT: effectiveBaseT,
      stampBaseH: effectiveStampBaseH,
      stampTextH: effectiveStampTextH,
      stampFitGap: effectiveStampFitGap,
    };
    const out = {};
    for (const k of DIMENSION_FIELDS) {
      const v = Number(current[k]);
      if (Number.isFinite(v) && v > 0) out[k] = v;
    }
    try {
      localStorage.setItem(DIMENSION_PRESET_KEY, JSON.stringify(out));
      setPresetMsg(t("cutter.presetSaved"));
    } catch {
      setPresetMsg(t("cutter.presetSaveFailed"));
    }
    setTimeout(() => setPresetMsg(""), 2000);
  }

  function resetDimensionPreset() {
    try { localStorage.removeItem(DIMENSION_PRESET_KEY); } catch {}
    setKnifeH(DIMENSION_DEFAULTS.knifeH);
    setKnifeT(DIMENSION_DEFAULTS.knifeT);
    setBaseH(DIMENSION_DEFAULTS.baseH);
    setBaseT(DIMENSION_DEFAULTS.baseT);
    setStampBaseH(DIMENSION_DEFAULTS.stampBaseH);
    setStampTextH(DIMENSION_DEFAULTS.stampTextH);
    setStampFitGap(DIMENSION_DEFAULTS.stampFitGap);
    setPresetMsg(t("cutter.presetReset"));
    setTimeout(() => setPresetMsg(""), 2000);
  }

  const handleDimensionChange = (setter, min, max) => (e) => {
    const raw = e.target.value;
    if (raw === "") { setter(""); return; }
    let effectiveValue = fromDisplayUnit(raw);
    // 타이핑 중엔 상한(max)만 제한하고 하한(min)은 blur에서 적용.
    // (min=6일 때 "10"을 입력하려고 "1"을 치면 즉시 6으로 바뀌던 버그 방지)
    if (max != null) {
      const n = Number(effectiveValue);
      if (Number.isFinite(n)) effectiveValue = Math.min(max, n);
    }
    // baseT 늘리기: 스케일이 최대면 현재 값보다 큰 값 거부
    if (setter === setBaseT) {
      const curScale = Number(scaleMm);
      if (Number.isFinite(curScale) && curScale >= SCALE_MAX_MM) {
        const newVal = Number(effectiveValue);
        const curVal = Number(baseT);
        if (Number.isFinite(newVal) && Number.isFinite(curVal) && newVal > curVal) return;
      }
    }
    setter(toBaseDimensionValue(effectiveValue));
  };

  // 입력 완료(blur) 시에만 하한(min) 적용 — 타이핑을 방해하지 않으면서 최종값은 범위 보장
  const handleDimensionBlur = (setter, min, max) => (e) => {
    const raw = e.target.value;
    if (raw === "") return;
    const n = Number(fromDisplayUnit(raw));
    if (!Number.isFinite(n)) return;
    const lo = min != null ? min : -Infinity;
    const hi = max != null ? max : Infinity;
    const clamped = Math.min(hi, Math.max(lo, n));
    if (clamped !== n) setter(toBaseDimensionValue(clamped));
  };

  const clampScaleMm = (value) => {
    return Math.min(SCALE_MAX_MM, Math.max(SCALE_MIN_MM, value));
  };

  const handleScaleBlur = () => {
    const currentScale = Number(scaleMm);
    const fallbackScale =
      Number.isFinite(prevScaleMmRef.current) && prevScaleMmRef.current > 0
        ? prevScaleMmRef.current
        : DEFAULT_TARGET_MM;

    const normalizedScale =
      Number.isFinite(currentScale) && currentScale > 0
        ? currentScale
        : fallbackScale;

    const clampedScale = Number(clampScaleMm(normalizedScale).toFixed(3));

    if (!Number.isFinite(currentScale) || Math.abs(currentScale - clampedScale) > 0.0005) {
      setScaleMm(clampedScale);
    }
  };

  const scaleMinByUnit =
    unit === "in" ? Number((SCALE_MIN_MM / MM_PER_IN).toFixed(3)) : SCALE_MIN_MM;
  const scaleMaxByUnit =
    unit === "in" ? Number((SCALE_MAX_MM / MM_PER_IN).toFixed(3)) : SCALE_MAX_MM;
  const unitLabel = unit === "in" ? "in" : "mm";

  /* ===== three.js refs ===== */
  const mountRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const labelRendererRef = useRef(null);
  const controlsRef = useRef(null);
  const gridOverlayGroupRef = useRef(null);
  const modelDimensionLabelGroupRef = useRef(null);
  const modelSizeLabelRef = useRef(null);

  // scene groups
  const modelRootRef = useRef(null);
  const stampGroupRef = useRef(null);
  const plainGroupRef = useRef(null);
  const cutterGroupRef = useRef(null);
  const scaleBoxSizeRef = useRef(null);

  // parts registry
  const partsRef = useRef({
    stampBase: [],
    stampFace: [],
    cutterWall: [],
    cutterBase: [],
  });

  // raycaster
  const raycasterRef = useRef(new THREE.Raycaster());
  const mouseNdcRef = useRef(new THREE.Vector2());

  const { user } = useContext(AuthContext);

  const location = useLocation();
  const history = useHistory();
  const paramsUrl = new URLSearchParams(location.search);
  const editFileId = paramsUrl.get("fileId");
  const galleryParamId = paramsUrl.get("gallery");

  // 🔥 Cutter 페이지에서 하단바 숨기기
  // 🔥 Cutter 페이지 전용 body 설정
  useEffect(() => {
    document.body.classList.add("hide-bottom-bar");
    if (window.innerWidth > 900) {
      document.body.classList.add("cutter-lock");
    }

    return () => {
      document.body.classList.remove("hide-bottom-bar");
      document.body.classList.remove("cutter-lock");
    };
  }, []);

  // 카메라를 대상 중심으로 재정렬
  function recenterCameraTo(obj) {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!obj || !camera || !controls) return;

    const box = new THREE.Box3().setFromObject(obj);
    if (box.isEmpty()) return;

    const size = new THREE.Vector3();
    const center = new THREE.Vector3();
    box.getSize(size);
    box.getCenter(center);

    // 👇 위로 70% 지점
    const target = new THREE.Vector3(
      center.x,
      box.min.y + size.y * 0.2,
      center.z
    );

    camera.position.copy(target).add(FIXED_VIEW_DIR.clone().multiplyScalar(FIXED_VIEW_DISTANCE));
    controls.target.copy(target);

    controls.update();
  }

  /* 3D 화면 보기 전환 — iso 는 처음 각도로 되돌린다 */
  function setView(kind) {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls) return;
    controls.autoRotate = false;
    setAutoRotate(false);
    setViewKind(kind);
    if (kind === "iso") {
      recenterCameraTo(modelRootRef.current);
      return;
    }
    const target = controls.target.clone();
    const dist = camera.position.distanceTo(target);
    const dir = kind === "top" ? new THREE.Vector3(0, 1, -0.001) : new THREE.Vector3(0, 0.35, -1);
    camera.position.copy(target).add(dir.normalize().multiplyScalar(dist));
    controls.update();
  }

  function zoomBy(factor) {
    const camera = cameraRef.current;
    const controls = controlsRef.current;
    if (!camera || !controls) return;
    const offset = camera.position.clone().sub(controls.target).multiplyScalar(factor);
    camera.position.copy(controls.target).add(offset);
    controls.update();
  }

  function toggleAutoRotate() {
    const controls = controlsRef.current;
    if (!controls) return;
    controls.autoRotate = !controls.autoRotate;
    controls.autoRotateSpeed = 2.5;
    setAutoRotate(controls.autoRotate);
  }

  /* 예시 그림으로 해보기 — public/home/ 의 SVG 를 파일처럼 넘긴다 */
  async function loadExample(file) {
    try {
      const res = await fetch(`/home/${file}`);
      const blob = await res.blob();
      handleSvgFile(new File([blob], file, { type: "image/svg+xml" }));
    } catch {}
  }


  //비회원용 ID
  function getGuestId() {
    let id = localStorage.getItem("guest_id");
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem("guest_id", id);
    }
    return `guest-${id}`;
  }

  // Shape 스케일 (px→mm)
  function scaleShapePoints(shape, sx, sy) {
    if (!shape?.curves) return;

    const scaleVec = (v) => {
      if (!v) return;
      if (typeof v.x === "number") v.x *= sx;
      if (typeof v.y === "number") v.y *= sy;
    };

    const walk = (sh) => {
      if (!sh?.curves) return;
      for (const c of sh.curves) {
        scaleVec(c.v0);
        scaleVec(c.v1);
        scaleVec(c.v2);
        scaleVec(c.v3);
      }
      if (Array.isArray(sh.holes)) {
        for (const h of sh.holes) walk(h);
      }
    };
    walk(shape);
  }

  /* ===== SVG 2D 기준 바운드 계산 (항상 고정 스케일 기준) =====
   - SVG 좌표계의 XY를 three 장면의 XZ로 사용*/
  function getXZBoundsFromShapes(shapes) {
    if (!shapes || !shapes.length) return null;

    let minX = Infinity,
      maxX = -Infinity,
      minY = Infinity,
      maxY = -Infinity;

    for (const sh of shapes) {
      const pts = sh.getSpacedPoints(256);
      for (const p of pts) {
        if (p.x < minX) minX = p.x;
        if (p.x > maxX) maxX = p.x;
        if (p.y < minY) minY = p.y; // y → z 로 간주
        if (p.y > maxY) maxY = p.y;
      }
    }

    if (
      !isFinite(minX) ||
      !isFinite(maxX) ||
      !isFinite(minY) ||
      !isFinite(maxY)
    )
      return null;

    return {
      minX,
      maxX,
      minZ: minY,
      maxZ: maxY,
      sizeX: maxX - minX,
      sizeZ: maxY - minY,
      centerX: (minX + maxX) / 2,
      centerZ: (minY + maxY) / 2,
    };
  }

  // bbox 계산 시 손잡이/plug 제외 (스케일 영향 안 받게)
  function boxExcludingHandle(obj) {
    const box = new THREE.Box3();
    obj.traverse((o) => {
      if (!o.isMesh) return;
      const pt = o.userData?.partType;
      if (pt === "stampHandle" || pt === "stampBasePlug") return;
      o.updateMatrixWorld(true);
      const mb = new THREE.Box3().setFromObject(o);
      if (!mb.isEmpty()) box.union(mb);
    });
    return box;
  }

  /* 손잡이 계열(손잡이·소켓·plug) 배율 — 커터 크기에 비례시킨다 (2026-09-10 김현비).
     OS 는 손잡이를 **커터 바깥 지름의 0.276배**로 만든다(convert.py `_attach_handles` 의
     handle_scale × 커터외경/기준80mm). 쿠키스토리는 원본 STL 을 그대로 써서 커터를 70 으로
     줄여도 손잡이가 22.31mm 로 남았다(도안 2종 실측 동일) — 그것을 같은 규칙으로 맞춘다.

     식이 왜 이렇게 떨어지나:
       최종 손잡이 = 26.58(원본) × partScale × s,  s = targetMm / 커터원본긴쪽 (fit 이 맞춤)
       목표        = targetMm × 0.276
       두 식을 같게 두면 **targetMm 이 약분**되어
       partScale = 0.276 × 커터원본긴쪽 / 26.58  — 입력 치수와 무관해진다.
     그래서 fit 전(원본 좌표)의 커터 bbox 만 있으면 정해진다.

     커터가 없는 모드(스탬프만)에서는 기준이 없으므로 1 (종전 그대로). */
  const HANDLE_TO_CUTTER_RATIO = 0.276;   // OS 와 같은 비율
  const HANDLE_NATIVE_MM = 26.58;         // cookie_handle_pocket_V2.STL 원본 지름

  function handlePartScale() {
    const cutterObj = cutterGroupRef.current;
    if (!cutterObj) return 1;
    cutterObj.updateWorldMatrix(true, true);
    const box = boxExcludingHandle(cutterObj);
    if (box.isEmpty()) return 1;
    const size = new THREE.Vector3();
    box.getSize(size);
    // fit 전이라 root.scale 이 아직 안 걸린 "원본 좌표" 크기다
    const longest = Math.max(size.x, size.z) / (cutterObj.parent?.scale?.x || 1);
    if (!(longest > 0)) return 1;
    const k = (HANDLE_TO_CUTTER_RATIO * longest) / HANDLE_NATIVE_MM;
    return Number.isFinite(k) && k > 0 ? k : 1;
  }

  function fitRootByShapeBounds(rootObj, targetMm) {
    if (!rootObj) return;

    rootObj.scale.set(1, 1, 1);
    rootObj.position.set(0, 0, 0);
    rootObj.rotation.set(0, 0, 0);
    rootObj.updateMatrixWorld(true);

    // 모드 무관하게 "커터 외곽 (벽+flange)" 기준으로 fit → 모드 바꿔도 물리 사이즈 동일
    let scaleBox = null;
    if (svgShapes && svgShapes.length) {
      const outerPath = getOuterSilhouettePath();
      if (outerPath) {
        try {
          const dynamicClearance = STAMP_BASE_EXPAND_MM + Number(effectiveStampFitGap || 0);
          const knifeT = Number(effectiveKnifeT || 0);
          const flangeW = Math.max(FLANGE_MIN_MM, Number(effectiveBaseT || 0));
          const totalOffset = dynamicClearance + knifeT + flangeW;

          const offsetted = pickBiggestPath(offsetPath(outerPath, totalOffset)) || outerPath;

          let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
          for (const p of offsetted) {
            const x = p.X / CLIPPER_SCALE;
            const y = p.Y / CLIPPER_SCALE;
            if (x < minX) minX = x;
            if (x > maxX) maxX = x;
            if (y < minY) minY = y;
            if (y > maxY) maxY = y;
          }
          // SVG XY 평면 → 회전 후 stamp X/Z (Y → -Z)
          scaleBox = new THREE.Box3(
            new THREE.Vector3(minX, 0, -maxY),
            new THREE.Vector3(maxX, 1, -minY)
          );
        } catch (e) {
          console.warn("[fit] cutter outer offset 계산 실패:", e);
        }
      }
    }

    if (!scaleBox) {
      // fallback: 모드별 bbox
      const scaleTarget =
        mode === "cutter"
          ? cutterGroupRef.current
          : mode === "stamp"
            ? plainGroupRef.current
            : modelRootRef.current;

      if (scaleTarget) {
        scaleTarget.updateWorldMatrix(true, true);
        const targetBox = boxExcludingHandle(scaleTarget);
        if (!targetBox.isEmpty()) scaleBox = targetBox;
      }
      if (!scaleBox) {
        scaleBox = boxExcludingHandle(rootObj);
      }
    }

    if (!scaleBox.isEmpty()) {
      const size = new THREE.Vector3();
      scaleBox.getSize(size);
      scaleBoxSizeRef.current = size.clone();
      const longest = Math.max(size.x, size.z);
      let s = Number(targetMm) / longest;

      if (Number.isFinite(s) && s > 0) {
        rootObj.scale.set(s, 1, s);

        /* 실제 커터 외곽으로 한 번 더 맞춘다 (2026-09-10 김현비 — OS 와 기준 통일).
           위 scaleBox 는 "이게 커터 외곽일 것" 이라고 **계산해서** 만든 박스라,
           그 계산이 실제 커터와 어긋나면 입력한 치수와 실물이 달라진다.
           실측(2026-09-10): 70mm 로 만든 커터가 **80.0mm** 로 나왔다 — 스탬프 기준으로
           걸려서 커터 외곽까지의 오프셋(갭 0.7 + 날 1.5 + 플랜지 3.0)이 통째로 더 붙었다.
           화면 라벨은 이 scaleBox 를 그대로 보여주므로 70 이라고 떠서 알아챌 수도 없었다.
           OS 는 `width_mm` 을 **커터 바깥 지름**으로 잡는다(iterate_then_render.py 의
           "스케일 기준=커터 외경") — 여기도 그것에 맞춘다.
           계산이 맞았던 경우엔 배율이 1.0 이라 아무 일도 일어나지 않는다. */
        const cutterObj = cutterGroupRef.current;
        if (cutterObj) {
          rootObj.updateMatrixWorld(true);
          const cutterBox = boxExcludingHandle(cutterObj);
          if (!cutterBox.isEmpty()) {
            const cutterSize = new THREE.Vector3();
            cutterBox.getSize(cutterSize);
            const cutterLongest = Math.max(cutterSize.x, cutterSize.z);
            if (cutterLongest > 0) {
              const fix = Number(targetMm) / cutterLongest;
              if (Number.isFinite(fix) && fix > 0 && Math.abs(fix - 1) > 1e-4) {
                s *= fix;
                rootObj.scale.set(s, 1, s);
              }
              /* 화면 치수 라벨(updateModelDimensionLabels)은 scaleBoxSizeRef × root.scale 로
                 W/D 를 만든다 — 보정 뒤에도 라벨이 **실제 커터 외곽**을 가리키도록 같이 고친다.
                 (안 고치면 라벨만 옛 계산값을 계속 보여준다) */
              scaleBoxSizeRef.current = cutterSize.clone().divideScalar(s / fix);
            }
          }
        }
      }
    }

    rootObj.updateMatrixWorld(true);
    let box = new THREE.Box3().setFromObject(rootObj);
    rootObj.position.y -= box.min.y;

    rootObj.updateMatrixWorld(true);
    box = new THREE.Box3().setFromObject(rootObj);

    const midX = (box.min.x + box.max.x) / 2;
    const midZ = (box.min.z + box.max.z) / 2;

    rootObj.position.x -= midX;
    rootObj.position.z -= midZ;
  }

  // SVG sanitize
  function sanitizeSvg(svg) {
    return svg
      .replace(/<script[\s\S]*?<\/script>/gi, "")
      .replace(/\son\w+="[^"]*"/gi, "")
      .replace(/<image\b[^>]*xlink:href="[^"]*"[^>]*>/gi, "");
  }

  // 파일로드
  async function handleSvgFile(file) {
    if (!file) return;

    const lower = file.name.toLowerCase();
    setFileName(file.name.replace(/\.png$/i, ".svg"));

    // ✅ PNG면 서버 변환
    if (lower.endsWith(".png")) {
      setSourceType("png");
      const API_BASE = process.env.REACT_APP_API_BASE_URL;

      const form = new FormData();
      form.append("file", file);
      if (user?.id) form.append("user_id", user.id);

      setIsConvertingSvg(true);

      try {
        const res = await fetch(`${API_BASE}/png_to_svg`, {
          method: "POST",
          body: form,
        });

        if (res.status === 429) {
          const err = await res.json().catch(() => ({}));
          showAlert(err.detail || t("cutter.aiDesignLimitReached") || "AI 디자인 일일 사용 한도에 도달했습니다.");
          return;
        }

        if (!res.ok) {
          showAlert(t("cutter.pngToSvgFailed"));
          return;
        }

        const svg = await res.text();
        const safe = sanitizeSvg(svg);
        setSvgText(safe);

        const blob = new Blob([safe], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);

        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }

        previewUrlRef.current = url;
        setPreviewUrl(url);
      } finally {
        setIsConvertingSvg(false);
      }

      return;
    }


    // ✅ SVG는 기존 로직 그대로
    if (!lower.endsWith(".svg")) return;
    setSourceType("svg");

    const reader = new FileReader();
    reader.onload = () => {
      let raw = String(reader.result || "");

      if (!/xmlns=/.test(raw) && /<svg/i.test(raw)) {
        raw = raw.replace(/<svg/i, '<svg xmlns="http://www.w3.org/2000/svg"');
      }

      const safe = sanitizeSvg(raw);
      setSvgText(safe);

      const blob = new Blob([safe], { type: "image/svg+xml" });
      const url = URL.createObjectURL(blob);

      if (previewUrlRef.current)
        URL.revokeObjectURL(previewUrlRef.current);

      previewUrlRef.current = url;
      setPreviewUrl(url);
    };
    reader.readAsText(file);
  }

  // AI 디자인에서 넘어온 SVG 자동 로드
  useEffect(() => {
    const designSvg = sessionStorage.getItem("designSvg");
    if (!designSvg) return;
    sessionStorage.removeItem("designSvg");

    const safe = sanitizeSvg(designSvg);
    setSvgText(safe);
    setSourceType("svg");
    setFileName("ai-design.svg");

    const blob = new Blob([safe], { type: "image/svg+xml" });
    const url = URL.createObjectURL(blob);
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    previewUrlRef.current = url;
    setPreviewUrl(url);
  }, []);

  function onUpload(e) {
    const file = e.target.files?.[0];
    if (file) handleSvgFile(file);
  }

  function handleDrop(e) {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleSvgFile(file);
  }

  function handleDragOver(e) {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  }

  function handleDragLeave(e) {
    e.preventDefault();
    setIsDragging(false);
  }

  function clearSvg() {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setSvgText("");
    setSourceType("svg");
    setFileName("");
    setPreviewUrl(null);
    setSelectedPart(null);

    clearGroup(plainGroupRef.current);
    clearGroup(cutterGroupRef.current);
    clearModelDimensionLabels();
    refreshPartsRegistry();
    applyHighlight();

    if (fileInputRef.current) fileInputRef.current.value = "";
  }

  // SVG → shapes(mm)
  const svgShapes = useMemo(() => {
    if (!svgText) return null;
    try {
      const parsed = new SVGLoader().parse(svgText);
      const shapes = [];

      for (const path of parsed.paths) {
        if (!path.userData) path.userData = { style: {} };
        if (!path.userData.style.fill || path.userData.style.fill === "none")
          path.userData.style.fill = "#000";

        let s = SVGLoader.createShapes(path);
        if (!s || s.length === 0) {
          const a = path.toShapes(true);
          const b = path.toShapes(true, true);
          s = a.length >= b.length ? a : b;
        }
        if (s?.length) shapes.push(...s);
      }
      if (!shapes.length) return null;

      // 정규화
      const tmp = new THREE.Group();
      for (const sh of shapes)
        tmp.add(new THREE.Mesh(new THREE.ShapeGeometry(sh)));

      tmp.updateMatrixWorld(true);
      const box = new THREE.Box3().setFromObject(tmp);
      const size = new THREE.Vector3();
      box.getSize(size);
      const longPx = Math.max(size.x, size.y);
      const longMm = longPx * PX_TO_MM;
      const ratio = longMm > 0 ? DEFAULT_TARGET_MM / longMm : 1;

      const sx = PX_TO_MM * ratio;
      const sy = PX_TO_MM * ratio;

      for (const sh of shapes) scaleShapePoints(sh, sx, sy);

      return shapes;
    } catch (e) {
      console.error("SVG parse error:", e);
      return null;
    }
  }, [svgText]);

  useEffect(() => {
    if (!svgText) return;

    // 저장해 둔 내 기본값이 있으면 그걸로, 없으면 기본 설계값으로 시작한다
    const preset = { ...DIMENSION_DEFAULTS, ...(readDimensionPreset() || {}) };
    if (knifeH === "") setKnifeH(preset.knifeH);
    if (knifeT === "") setKnifeT(preset.knifeT);
    if (baseH === "") setBaseH(preset.baseH);
    if (baseT === "") setBaseT(preset.baseT);
    if (stampBaseH === "") setStampBaseH(preset.stampBaseH);
    if (stampTextH === "") setStampTextH(preset.stampTextH);
    if (stampFitGap === "") setStampFitGap(preset.stampFitGap);
  }, [svgText]);

  // SVG 업로드 후 스케일 초기값
  useEffect(() => {
    if (svgShapes) {
      setScaleMm(DEFAULT_TARGET_MM);
      setDimensionBaseScaleMm(DEFAULT_TARGET_MM);
    }
  }, [svgShapes]);

  // 손잡이/홀 STL 사전 로드 (한 번만)
  useEffect(() => {
    let cancelled = false;
    preloadHandleHoleGeometries()
      .then(({ plugHeight }) => {
        if (cancelled) return;
        handleHoleReadyRef.current = true;
        if (Number.isFinite(plugHeight)) plugHeightRef.current = plugHeight;
        setHandleHoleReady(true);
      })
      .catch((e) => console.error("[Cutter] handle/hole STL load 실패:", e));
    return () => { cancelled = true; };
  }, []);

  /* ===== Layout & renderer ===== */

  function resize3D() {
    const mount = mountRef.current;
    const r = rendererRef.current;
    const lr = labelRendererRef.current;
    const cam = cameraRef.current;
    if (!mount || !r || !cam) return;

    const w = mount.clientWidth;
    const h = mount.clientHeight;
    r.setSize(w, h);
    if (lr) lr.setSize(w, h);
    cam.aspect = w / h;
    cam.updateProjectionMatrix();
  }

  function onDragStart(e) {
    const startX = e.touches ? e.touches[0].clientX : e.clientX;
    const startW = sidebarW;

    function onMove(ev) {
      const x = ev.touches ? ev.touches[0].clientX : ev.clientX;
      const next = Math.min(
        MAX_SIDEBAR,
        Math.max(MIN_SIDEBAR, startW + (x - startX))
      );
      setSidebarW(next);
      resize3D();
    }

    function onEnd() {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onEnd);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onEnd);
    }

    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onEnd);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("touchend", onEnd);
  }

  useEffect(() => {
    resize3D();
  }, [sidebarW]);

  /* ===== three.js scene 구축 ===== */
  useEffect(() => {
    const mount = mountRef.current;

    const w = mount.clientWidth;
    const h = mount.clientHeight;

    /* ─ Renderer ─ */
    const renderer = new THREE.WebGLRenderer({
      antialias: true,
      preserveDrawingBuffer: true,   // ← 이거 없어서 검정 썸네일 뜸
      alpha: true,
      logarithmicDepthBuffer: true,
    });

    renderer.outputColorSpace = THREE.SRGBColorSpace;
    renderer.toneMapping = THREE.NoToneMapping;
    renderer.toneMappingExposure = 1;

    THREE.ColorManagement.enabled = true;

    ;
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(w, h);
    mount.appendChild(renderer.domElement);

    const labelRenderer = new CSS2DRenderer();
    labelRenderer.setSize(w, h);
    labelRenderer.domElement.className = "ckc-labels";
    labelRenderer.domElement.style.position = "absolute";
    labelRenderer.domElement.style.top = "0";
    labelRenderer.domElement.style.left = "0";
    labelRenderer.domElement.style.pointerEvents = "none";
    mount.appendChild(labelRenderer.domElement);

    /* ─ Scene ─ */
    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0xfbf6ee);

    /* ─ Camera ─ */
    const camera = new THREE.PerspectiveCamera(45, w / h, 1, 1000);
    camera.position.copy(new THREE.Vector3(0, 0, 0)).add(FIXED_VIEW_DIR.clone().multiplyScalar(FIXED_VIEW_DISTANCE));

    /* ─ Controls ─ */
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.target.set(0, -60, 0);

    // 메인 키 라이트 (색 살림)
    const keyLight = new THREE.DirectionalLight(0xffffff, 0.75);
    keyLight.position.set(120, 180, 100);
    scene.add(keyLight);

    // 보조 필 라이트 (그림자만 완화)
    const fillLight = new THREE.DirectionalLight(0xffffff, 0.25);
    fillLight.position.set(-120, 80, -100);
    scene.add(fillLight);

    // 약한 앰비언트 (흰끼 방지)
    const amb = new THREE.AmbientLight(0xffffff, 0.15);
    scene.add(amb);

    /* ─ Grid / Ground ─ */
    // 프린터 베드 크기 250×250mm 반영 (10mm 간격 = 25칸)
    const grid = new THREE.GridHelper(250, 25, 0xffffff, 0xffffff);
    grid.material.opacity = 0.5;
    grid.position.y = 0.05;
    grid.material.depthWrite = false;
    grid.material.transparent = true;
    grid.material.opacity = 0.9;
    scene.add(grid);

    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(BUILD_PLATE_SIZE_MM, BUILD_PLATE_SIZE_MM),
      new THREE.MeshBasicMaterial({
        color: 0xF4E5D6,
      })
    );

    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);

    ensureGridCoordinateLabels(scene);

    /* ─ Root Group ─ */
    const modelRoot = new THREE.Group();
    modelRoot.visible = false;
    const plainGroup = new THREE.Group();
    const cutterGroup = new THREE.Group();

    modelRoot.add(plainGroup);
    modelRoot.add(cutterGroup);
    scene.add(modelRoot);

    /* <ref 등록> */
    sceneRef.current = scene;
    cameraRef.current = camera;
    rendererRef.current = renderer;
    labelRendererRef.current = labelRenderer;
    controlsRef.current = controls;

    modelRootRef.current = modelRoot;
    stampGroupRef.current = plainGroup;
    plainGroupRef.current = plainGroup;
    cutterGroupRef.current = cutterGroup;

    /* ===== Raycasting (파트 선택) ===== */
    function handlePointerDown(ev) {
      const rect = renderer.domElement.getBoundingClientRect();
      const x = ((ev.clientX - rect.left) / rect.width) * 2 - 1;
      const y = -((ev.clientY - rect.top) / rect.height) * 2 + 1;

      mouseNdcRef.current.set(x, y);
      raycasterRef.current.setFromCamera(mouseNdcRef.current, camera);

      const intersects = raycasterRef.current.intersectObjects(
        modelRoot.children,
        true
      );

      if (intersects.length) {
        const hit = intersects.find((it) => it.object?.userData?.partType && it.object.userData.partType !== "stampHandle");
        if (hit) setSelectedPart(hit.object.userData.partType);
        else setSelectedPart(null);
      } else {
        setSelectedPart(null);
      }
    }

    renderer.domElement.addEventListener("pointerdown", handlePointerDown);

    /* ===== Resize handler ===== */
    const onResize = () => resize3D();
    window.addEventListener("resize", onResize);

    /* ===== Animate ===== */
    const tick = () => {
      controls.update();
      renderer.render(scene, camera);
      labelRenderer.render(scene, camera);
      requestAnimationFrame(tick);
    };
    tick();

    /* ===== Cleanup ===== */
    return () => {
      window.removeEventListener("resize", onResize);
      renderer.domElement.removeEventListener("pointerdown", handlePointerDown);
      if (gridOverlayGroupRef.current) {
        clearGridCoordinateLabels();
        gridOverlayGroupRef.current.removeFromParent();
        gridOverlayGroupRef.current = null;
      }
      clearModelDimensionLabels();
      controls.dispose();
      renderer.dispose();
      labelRendererRef.current = null;
      mount.removeChild(renderer.domElement);
      mount.removeChild(labelRenderer.domElement);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
    };
  }, []);

  /* ===== Clipper helpers ===== */

  const pickOuterMostShape = (shapes) => {
    let best = null;
    let area = -Infinity;
    for (const sh of shapes) {
      const pts = sh.getPoints(128);
      if (pts.length < 3) continue;
      const a = Math.abs(THREE.ShapeUtils.area(pts));
      if (a > area) {
        area = a;
        best = sh;
      }
    }
    return best;
  };

  function shapeToClipperPath(shape, segs = Math.min(effectiveCurveSegments * 3, 720)) {
    const pts = shape.getSpacedPoints(segs);
    return pts.map((p) => ({
      X: Math.round(p.x * CLIPPER_SCALE),
      Y: Math.round(p.y * CLIPPER_SCALE),
    }));
  }

  function clipperPathToThreeShape(path) {
    return new THREE.Shape(
      path.map(
        (p) => new THREE.Vector2(p.X / CLIPPER_SCALE, p.Y / CLIPPER_SCALE)
      )
    );
  }

  function pickBiggestPath(paths) {
    if (!paths?.length) return null;
    return paths
      .slice()
      .sort(
        (a, b) =>
          Math.abs(ClipperLib.Clipper.Area(b)) -
          Math.abs(ClipperLib.Clipper.Area(a))
      )[0];
  }

  function ensureCCW(path) {
    if (!ClipperLib.Clipper.Orientation(path)) return path.slice().reverse();
    return path;
  }

  // ArcTolerance는 좌표 단위(mm×CLIPPER_SCALE)로 해석된다.
  // 라운드 조인 곡선과 다각형 근사의 최대 오차(mm). 0.02mm면 프린트/육안상 완전 매끈.
  const ARC_TOLERANCE_MM = 0.02;

  function offsetPath(path, offMm) {
    const co = new ClipperLib.ClipperOffset(2, ARC_TOLERANCE_MM * CLIPPER_SCALE);
    co.AddPath(
      ensureCCW(path),
      ClipperLib.JoinType.jtRound,
      ClipperLib.EndType.etClosedPolygon
    );
    const sol = new ClipperLib.Paths();
    co.Execute(sol, Math.round(offMm * CLIPPER_SCALE));
    return sol;
  }

  // 도장면 윤곽을 "길이 기준 균일 간격"으로 재샘플링.
  // 커터 벽과 같은 방식이라 완만한 곡선도 각지지 않으면서, 트레이싱 과샘플링/노이즈를 제거.
  // chordMm: 인접 점 사이 목표 간격(mm). 작을수록 매끈, 클수록 가벼움.
  function resampleShape(shape, chordMm) {
    try {
      const outerLen = shape.getLength();
      if (!(outerLen > 0)) return shape;
      const nOuter = Math.max(12, Math.ceil(outerLen / chordMm));
      const ns = new THREE.Shape(shape.getSpacedPoints(nOuter));
      ns.holes = (shape.holes || []).map((h) => {
        const len = h.getLength();
        const n = Math.max(8, Math.ceil((len > 0 ? len : chordMm * 8) / chordMm));
        return new THREE.Path(h.getSpacedPoints(n));
      });
      return ns;
    } catch (e) {
      return shape;
    }
  }

  function makeRingShape(outerPath, innerPath) {
    const out = clipperPathToThreeShape(outerPath);
    if (innerPath) out.holes.push(clipperPathToThreeShape(innerPath));
    out.autoClose = true;
    return out;
  }

  function buildShapeFromClipperPath(path) {
    const shape = new THREE.Shape(
      path.map(
        (p) => new THREE.Vector2(p.X / CLIPPER_SCALE, p.Y / CLIPPER_SCALE)
      )
    );
    shape.autoClose = true;
    return shape;
  }

  /* ===== 바깥 실루엣(커터 절단선) 복원 =====
     그림 전체를 union했을 때 바깥 윤곽이 하나면 그대로 쓴다 — 기존 동작과 같다.
     그런데 털처럼 짧은 획으로 실루엣을 그린 그림(AI 생성물·손그림 스캔)은
     획 하나하나가 따로 놀아서 전체를 감싸는 윤곽이 아예 없다. 그러면 "제일 큰 조각"이
     몸통 덩어리로 잡혀 커터가 엉뚱한 모양으로 나온다.
     그럴 때만 살짝 부풀렸다 되돌려(모폴로지 클로징) 끊긴 틈을 메운다.
     붙는 데 필요한 최소 폭만 쓰도록 작은 값부터 올린다. */
  const SILHOUETTE_CLOSE_STEPS_MM = [0.4, 0.8, 1.2, 1.8, 2.5, 3.5];
  const silhouetteCacheRef = useRef({ shapes: null, segs: null, path: null });

  function unionAllShapePaths() {
    if (!svgShapes?.length) return null;
    const segs = Math.min(effectiveCurveSegments * 3, 720);
    const subject = [];
    for (const sh of svgShapes) {
      const pts = sh.getSpacedPoints(segs);
      if (pts.length < 3) continue;
      subject.push(
        ensureCCW(
          pts.map((p) => ({
            X: Math.round(p.x * CLIPPER_SCALE),
            Y: Math.round(p.y * CLIPPER_SCALE),
          }))
        )
      );
    }
    if (!subject.length) return null;

    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(subject, ClipperLib.PolyType.ptSubject, true);
    const sol = new ClipperLib.Paths();
    const ok = clipper.Execute(
      ClipperLib.ClipType.ctUnion,
      sol,
      ClipperLib.PolyFillType.pftNonZero,
      ClipperLib.PolyFillType.pftNonZero
    );
    return ok && sol.length ? sol : null;
  }

  /* union 결과에서 바깥 윤곽만 (구멍은 반대 방향이라 걸러진다) */
  function outerContoursOf(paths) {
    return paths.filter((p) => ClipperLib.Clipper.Orientation(p));
  }

  /* 실루엣이 성한지 판정 — "가장 큰 윤곽이 나머지를 전부 품고 있는가".
     선화는 눈·코 같은 내부 디테일도 각자 별개 윤곽이라 개수로는 구분이 안 된다.
     외곽선이 이어져 있으면 그 하나가 나머지를 다 감싸고, 끊겨 있으면 못 감싼다.
     감싸고 있으면 그 윤곽을, 아니면 null을 돌려준다. */
  function enclosingContourOf(paths) {
    const outers = outerContoursOf(paths);
    if (!outers.length) return null;
    const biggest = pickBiggestPath(outers);
    const rest = outers.filter((p) => p !== biggest);
    if (!rest.length) return biggest;

    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(rest, ClipperLib.PolyType.ptSubject, true);
    clipper.AddPath(biggest, ClipperLib.PolyType.ptClip, true);
    const outside = new ClipperLib.Paths();
    clipper.Execute(
      ClipperLib.ClipType.ctDifference,
      outside,
      ClipperLib.PolyFillType.pftNonZero,
      ClipperLib.PolyFillType.pftNonZero
    );

    const strayMm2 =
      outside.reduce((s, p) => s + Math.abs(ClipperLib.Clipper.Area(p)), 0) /
      (CLIPPER_SCALE * CLIPPER_SCALE);
    return strayMm2 < 0.5 ? biggest : null; // 0.5mm² 미만은 트레이싱 오차로 본다
  }

  /* gMm만큼 부풀렸다가 그만큼 되돌린다 → 2*gMm보다 좁은 틈이 메워진다 */
  function closeGaps(paths, gMm) {
    const grow = new ClipperLib.ClipperOffset(2, ARC_TOLERANCE_MM * CLIPPER_SCALE);
    grow.AddPaths(paths, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
    const grown = new ClipperLib.Paths();
    grow.Execute(grown, Math.round(gMm * CLIPPER_SCALE));
    if (!grown.length) return null;

    const shrink = new ClipperLib.ClipperOffset(2, ARC_TOLERANCE_MM * CLIPPER_SCALE);
    shrink.AddPaths(grown, ClipperLib.JoinType.jtRound, ClipperLib.EndType.etClosedPolygon);
    const shrunk = new ClipperLib.Paths();
    shrink.Execute(shrunk, Math.round(-gMm * CLIPPER_SCALE));
    return shrunk.length ? shrunk : null;
  }

  function getOuterSilhouettePath() {
    const cache = silhouetteCacheRef.current;
    if (cache.shapes === svgShapes && cache.segs === effectiveCurveSegments) return cache.path;

    let path = null;
    const union = unionAllShapePaths();
    if (union) {
      path = enclosingContourOf(union); // 성한 그림이면 여기서 끝 — 손대지 않는다
      if (!path) {
        for (const g of SILHOUETTE_CLOSE_STEPS_MM) {
          const closed = closeGaps(union, g);
          const joined = closed ? enclosingContourOf(closed) : null;
          if (joined) {
            console.info(`[커터] 바깥선이 끊겨 있어 ${g}mm로 이어붙임`);
            path = joined;
            break;
          }
        }
        if (!path) {
          console.warn("[커터] 끊긴 바깥선을 잇지 못함 → 가장 큰 조각 사용");
          path = pickBiggestPath(outerContoursOf(union));
        }
      }
    }

    silhouetteCacheRef.current = { shapes: svgShapes, segs: effectiveCurveSegments, path };
    return path;
  }

  function getStampOuterUnionPaths() {
    if (!svgShapes?.length) return null;

    const subjectPaths = [];
    for (const sh of svgShapes) {
      const pts = sh.getSpacedPoints(Math.min(effectiveCurveSegments * 3, 720));
      if (pts.length < 3) continue;

      let path = pts.map((p) => ({
        X: Math.round(p.x * CLIPPER_SCALE),
        Y: Math.round(p.y * CLIPPER_SCALE),
      }));

      if (!ClipperLib.Clipper.Orientation(path)) path = path.reverse();
      subjectPaths.push(path);
    }
    if (!subjectPaths.length) return null;

    const clipper = new ClipperLib.Clipper();
    clipper.AddPaths(subjectPaths, ClipperLib.PolyType.ptSubject, true);

    const solution = new ClipperLib.Paths();
    const ok = clipper.Execute(
      ClipperLib.ClipType.ctUnion,
      solution,
      ClipperLib.PolyFillType.pftNonZero,
      ClipperLib.PolyFillType.pftNonZero
    );

    if (!ok || !solution.length) return null;
    return solution.map((path) => ensureCCW(path));
  }

  function stripExtrudeCapFaces(geometry, depth) {
    if (!geometry?.index || !geometry?.attributes?.position) return geometry;

    const pos = geometry.attributes.position;
    const src = geometry.index.array;
    const next = [];
    const d = Number(depth);
    const eps = Math.max(1e-6, Math.abs(d) * 1e-5);

    for (let i = 0; i < src.length; i += 3) {
      const ia = src[i];
      const ib = src[i + 1];
      const ic = src[i + 2];

      const za = pos.getZ(ia);
      const zb = pos.getZ(ib);
      const zc = pos.getZ(ic);

      const isBottom =
        Math.abs(za) <= eps && Math.abs(zb) <= eps && Math.abs(zc) <= eps;
      if (isBottom) continue;
      next.push(ia, ib, ic);
    }

    geometry.setIndex(next);
    geometry.computeVertexNormals();
    return geometry;
  }

  /* ===== Group utils ===== */

  function clearGroup(group) {
    if (!group) return;
    while (group.children.length) {
      const c = group.children.pop();
      c.geometry?.dispose?.();
      if (Array.isArray(c.material))
        c.material.forEach((m) => m.dispose?.());
      else c.material?.dispose?.();
    }
  }

  function makeGridLabelElement(text) {
    const el = document.createElement("div");
    el.className = "ckc-grid-label";
    el.textContent = text;
    el.style.padding = "0";
    el.style.border = "none";
    el.style.borderRadius = "0";
    el.style.background = "transparent";
    el.style.color = "#9ca3af";
    el.style.fontSize = "13px";
    el.style.fontWeight = "500";
    el.style.pointerEvents = "none";
    return el;
  }

  function clearGridCoordinateLabels() {
    const group = gridOverlayGroupRef.current;
    if (!group) return;
    while (group.children.length) {
      const child = group.children[0];
      child.removeFromParent();
    }
  }

  function ensureGridCoordinateLabels(scene) {
    if (!scene) return;

    if (!gridOverlayGroupRef.current) {
      const group = new THREE.Group();
      group.name = "grid-overlay-labels";
      scene.add(group);
      gridOverlayGroupRef.current = group;
    }

    clearGridCoordinateLabels();

    const group = gridOverlayGroupRef.current;
    const size = BUILD_PLATE_SIZE_MM;
    const half = size / 2;
    const edgeOffset = 7;
    const y = 0.3;
    const formatGridLabel = (mmValue) => {
      if (unit === "in") return mmToIn(mmValue).toFixed(2);
      return `${mmValue}`;
    };

    // X축 하단 (0 ~ 200 표시, 좌→우 = 0→200)
    for (let x = 0; x <= size; x += GRID_LABEL_STEP_MM) {
      const worldX = half - x;
      const bottomLabel = new CSS2DObject(makeGridLabelElement(formatGridLabel(x)));
      bottomLabel.position.set(worldX, y, -half - edgeOffset);
      group.add(bottomLabel);
    }

    // Z축 좌측 (0 ~ 200 표시)
    for (let z = GRID_LABEL_STEP_MM; z <= size; z += GRID_LABEL_STEP_MM) {
      const worldZ = z - half;
      const leftLabel = new CSS2DObject(makeGridLabelElement(formatGridLabel(z)));
      leftLabel.position.set(half + edgeOffset, y, worldZ);
      group.add(leftLabel);
    }
  }

  function makeModelDimensionLabelElement(text) {
    const el = document.createElement("div");
    el.textContent = text;
    el.style.padding = "3px 7px";
    el.style.border = "1px solid #cbd5e1";
    el.style.borderRadius = "6px";
    el.style.background = "rgba(255, 255, 255, 0.9)";
    el.style.color = "#1f2937";
    el.style.fontSize = "12px";
    el.style.fontWeight = "600";
    el.style.pointerEvents = "none";
    return el;
  }

  function ensureModelDimensionLabels(scene) {
    if (!scene) return;
    const labelRenderer = labelRendererRef.current;
    if (!labelRenderer) return;
    if (modelSizeLabelRef.current) return;

    const sizeLabelEl = makeModelDimensionLabelElement("W 0 mm · D 0 mm · H 0 mm");
    sizeLabelEl.style.position = "absolute";
    sizeLabelEl.style.bottom = "30px";
    sizeLabelEl.style.left = "50%";
    sizeLabelEl.style.zIndex = "10";
    sizeLabelEl.style.transform = "translateX(-50%)";
    sizeLabelEl.style.display = "none"; // 리뉴얼: 치수는 React 의 .ckc-dims 가 보여준다

    labelRenderer.domElement.appendChild(sizeLabelEl);

    modelDimensionLabelGroupRef.current = labelRenderer.domElement;
    modelSizeLabelRef.current = sizeLabelEl;
  }

  function clearModelDimensionLabels() {
    const sizeLabelEl = modelSizeLabelRef.current;
    if (sizeLabelEl && sizeLabelEl.parentNode) {
      sizeLabelEl.parentNode.removeChild(sizeLabelEl);
    }
    modelDimensionLabelGroupRef.current = null;
    modelSizeLabelRef.current = null;
  }

  function updateModelDimensionLabels(root, { syncScale = false } = {}) {
    const scene = sceneRef.current;
    if (!scene || !root) return;

    ensureModelDimensionLabels(scene);

    const sizeLabelEl = modelSizeLabelRef.current;
    if (!sizeLabelEl) return;

    // W/D: scaleBox(커터 외곽 기준) × root.scale → 모든 모드에서 동일
    const sbs = scaleBoxSizeRef.current;
    const rootScale = root.scale?.x ?? 1;

    // H: 실제 메쉬 bbox (손잡이/plug 제외)
    const hBox = boxExcludingHandle(root);

    if ((!sbs || !Number.isFinite(rootScale) || rootScale <= 0) && hBox.isEmpty()) return;

    let widthMm, depthMm;
    if (sbs && Number.isFinite(rootScale) && rootScale > 0) {
      widthMm = sbs.x * rootScale;
      depthMm = sbs.z * rootScale;
    } else {
      widthMm = hBox.isEmpty() ? 0 : (hBox.max.x - hBox.min.x);
      depthMm = hBox.isEmpty() ? 0 : (hBox.max.z - hBox.min.z);
    }

    const heightMm = hBox.isEmpty() ? 0 : (hBox.max.y - hBox.min.y);

    setModelDims((prev) =>
      prev && Math.abs(prev.w - widthMm) < 0.01 && Math.abs(prev.d - depthMm) < 0.01 && Math.abs(prev.h - heightMm) < 0.01
        ? prev
        : { w: widthMm, d: depthMm, h: heightMm }
    );

    if (syncScale) {
      const longestSide = Math.max(widthMm, depthMm);
      if (Number.isFinite(longestSide) && longestSide > 0) {
        const syncedScale = Number(longestSide.toFixed(2));
        const currentScale = Number(scaleMm);
        if (
          !Number.isFinite(currentScale) ||
          Math.abs(currentScale - syncedScale) > 0.005
        ) {
          isScaleSyncUpdateRef.current = true;
          setScaleMm(syncedScale);
        }
      }
    }

    const width = unit === "in" ? mmToIn(widthMm) : widthMm;
    const depth = unit === "in" ? mmToIn(depthMm) : depthMm;
    const height = unit === "in" ? mmToIn(heightMm) : heightMm;
    const handleNote = (mode === "stamp" || mode === "both") ? ` (${t("cutter.handleExcluded", "손잡이 제외")})` : "";
    sizeLabelEl.textContent = `W ${width.toFixed(2)} ${unitLabel} · D ${depth.toFixed(2)} ${unitLabel} · H ${height.toFixed(2)} ${unitLabel}${handleNote}`;
  }

  /* ===== Build: 스탬프 바닥(전체 내부 꽉 채워서 Union) ===== */
  function buildStampBaseFromOuter() {
    const group = plainGroupRef.current;
    if (!group || !svgShapes?.length) return null;

    const baseDepth = Math.max(0, Number(effectiveStampBaseH) || 0);
    if (baseDepth <= 0) return null;

    const outerPath = getOuterSilhouettePath();
    if (!outerPath) return null;

    // 스탬프 베이스를 도안 외곽보다 바깥으로 offset(1.5mm)해서 빌드
    const expandedPath = pickBiggestPath(offsetPath(outerPath, STAMP_BASE_EXPAND_MM));
    const baseShape = clipperPathToThreeShape(expandedPath || outerPath);
    baseShape.holes = [];

    const baseMat = new THREE.MeshStandardMaterial({
      color: 0xF3E57C,
      shininess: 20,
    });

    let geo = new THREE.ExtrudeGeometry(baseShape, {
      depth: baseDepth,
      bevelEnabled: false,
      curveSegments: effectiveCurveSegments,
      steps: 1,
    });

    geo.rotateX(-Math.PI / 2);

    const mesh = new THREE.Mesh(geo, baseMat.clone());
    mesh.userData.partType = "stampBase";
    // handleHole.js에서 hole을 박은 ExtrudeGeometry로 재생성하기 위해 원본 보존
    mesh.userData.baseShape = baseShape;
    mesh.userData.baseDepth = baseDepth;
    mesh.userData.curveSegments = effectiveCurveSegments;
    group.add(mesh);
    return mesh;
  }

  /* ===== Build: 도장면 ===== */
  function buildPlainGroup() {
    const group = plainGroupRef.current;
    if (!group || !svgShapes?.length) return null;

    const depth = Math.max(0, Number(effectiveStampTextH) || 0);
    if (depth <= 0) return null;
    const baseDepth = Math.max(0, Number(effectiveStampBaseH) || 0);

    const mat = new THREE.MeshPhongMaterial({
      color: 0xF2A1B3,
      shininess: 20,
      side: THREE.DoubleSide,
    });

    const faceGroup = new THREE.Group();
    const outerShape = !stampOuterInclude ? pickOuterMostShape(svgShapes) : null;

    for (const sh of svgShapes) {
      if (outerShape && sh === outerShape) continue;
      const faceShape = resampleShape(sh, FACE_CHORD_MM);
      let geo = new THREE.ExtrudeGeometry(faceShape, {
        depth,
        bevelEnabled: false,
        curveSegments: effectiveCurveSegments,
      });

      geo.deleteAttribute("normal");
      geo = BufferGeometryUtils.mergeVertices(geo);
      geo.computeVertexNormals();

      geo.rotateX(-Math.PI / 2);
      geo.translate(0, baseDepth, 0);

      const mesh = new THREE.Mesh(geo, mat.clone());
      mesh.userData.partType = "stampFace";
      // STL 내보내기에서 도장면을 베이스와 이어 붙일 때 필요
      mesh.userData.faceShape = faceShape;
      mesh.userData.faceDepth = depth;
      faceGroup.add(mesh);
    }

    if (!faceGroup.children.length) return null;

    group.add(faceGroup);
    return faceGroup;
  }

  /* ===== Build: 커터(벽 + 베이스) ===== */
  function buildCutterGroup() {
    const group = cutterGroupRef.current;
    if (!group || !svgShapes?.length) return null;

    clearGroup(group);

    const basePath = getOuterSilhouettePath();
    if (!basePath) return null;

    /* 벽 offset 계산 — 스탬프 바닥 확장(1.5mm) + 끼움 여유 */
    const dynamicClearance = STAMP_BASE_EXPAND_MM + effectiveStampFitGap;

    const innerShift =
      pickBiggestPath(offsetPath(basePath, dynamicClearance)) || basePath;

    const wallOuter = pickBiggestPath(
      offsetPath(basePath, dynamicClearance + Number(effectiveKnifeT || 0))
    );

    if (!innerShift || !wallOuter) return null;

    /* 벽 geometry */
    const wallRing = makeRingShape(wallOuter, innerShift);
    let wallGeo = new THREE.ExtrudeGeometry(wallRing, {
      depth: Math.max(0, Number(effectiveKnifeH) || 0),
      bevelEnabled: false,
      curveSegments: cutterCurveSegments,
    });

    wallGeo.deleteAttribute("normal");
    wallGeo = BufferGeometryUtils.mergeVertices(wallGeo);
    wallGeo.computeVertexNormals();
    wallGeo.rotateX(-Math.PI / 2);

    const wallMat = new THREE.MeshPhongMaterial({
      color: 0x7ECFC0,
      shininess: 5,
    });

    const wallMesh = new THREE.Mesh(wallGeo, wallMat);
    wallMesh.userData.partType = "cutterWall";
    // STL 내보내기에서 벽+플랜지를 이음매 없는 한 솔리드로 다시 만들 때 필요
    wallMesh.userData.cutter = {
      innerShape: clipperPathToThreeShape(innerShift),
      wallShape: clipperPathToThreeShape(wallOuter),
      knifeH: Math.max(0, Number(effectiveKnifeH) || 0),
      curveSegments: cutterCurveSegments,
    };

    /* 베이스 (flange) */
    const flangeW = Math.max(FLANGE_MIN_MM, Number(effectiveBaseT || 0));
    const baseOuter = pickBiggestPath(offsetPath(wallOuter, flangeW));

    let baseMesh = null;

    if (baseOuter) {
      const baseRing = makeRingShape(baseOuter, wallOuter);
      let baseGeo = new THREE.ExtrudeGeometry(baseRing, {
        depth: Math.max(0, Number(effectiveBaseH) || 0),
        bevelEnabled: false,
        curveSegments: cutterCurveSegments,
      });

      baseGeo.deleteAttribute("normal");
      baseGeo = BufferGeometryUtils.mergeVertices(baseGeo);
      baseGeo.computeVertexNormals();

      baseGeo.rotateX(-Math.PI / 2);

      const baseMat = new THREE.MeshPhongMaterial({
        color: 0x9BCDF0,
        shininess: 10,
      });

      baseMesh = new THREE.Mesh(baseGeo, baseMat);
      baseMesh.userData.partType = "cutterBase";
      wallMesh.userData.cutter.baseShape = clipperPathToThreeShape(baseOuter);
      wallMesh.userData.cutter.baseH = Math.max(0, Number(effectiveBaseH) || 0);
    }

    const root = new THREE.Group();
    root.add(wallMesh);
    if (baseMesh) root.add(baseMesh);

    group.add(root);
    return root;
  }

  function refreshPartsRegistry() {
    const reg = partsRef.current;
    reg.stampBase = [];
    reg.stampFace = [];
    reg.cutterWall = [];
    reg.cutterBase = [];

    modelRootRef.current?.traverse((obj) => {
      if (obj.isMesh && obj.userData?.partType) {
        const k = obj.userData.partType;
        if (reg[k]) reg[k].push(obj);
      }
    });
  }

  /* ===== 전체 재빌드 ===== */
  function rebuildAll({ recenter = false, applyFit = false, syncScale = false } = {}) {
    if (!svgText || !svgShapes) return;

    const root = modelRootRef.current;
    if (!root) return;

    /* 기존 그룹 클리어 */
    clearGroup(plainGroupRef.current);
    clearGroup(cutterGroupRef.current);

    let baseRaw = null;
    let plainRaw = null;
    let cutterRaw = null;

    /* 생성 모드 */
    if (mode === "cutter") {
      cutterRaw = buildCutterGroup();
    } else if (mode === "stamp") {
      baseRaw = buildStampBaseFromOuter();
      plainRaw = buildPlainGroup();
    } else {
      baseRaw = buildStampBaseFromOuter();
      plainRaw = buildPlainGroup();
      cutterRaw = buildCutterGroup();
    }

    /* 스탬프 손잡이/홀 추가 (mode === stamp 또는 both, STL 로드됨) */
    if ((mode === "stamp" || mode === "both") && handleHoleReadyRef.current) {
      addHandleAndHole(plainGroupRef.current, { scale: handlePartScale() });
    }

    /* 파츠 등록 (커터 베이스 기준 스케일 계산에 필요) */
    refreshPartsRegistry();

    /* 스케일 정렬 (scaleMm 변경 시에만 실행) */
    if (applyFit) {
      fitRootByShapeBounds(root, scaleMm);
    }

    /* 손잡이 Y 스케일 보정: root가 (s,1,s)이므로 handle.scale.y = s 로 균등 스케일 복원 */
    const rootS = root.scale.x;
    if (Number.isFinite(rootS) && rootS > 0) {
      root.traverse((obj) => {
        if (!obj.isMesh || obj.userData?.partType !== "stampHandle") return;
        obj.scale.y = rootS;
        const { nativeMinY, stampNativeMinY } = obj.userData;
        if (Number.isFinite(nativeMinY) && Number.isFinite(stampNativeMinY)) {
          obj.position.y = stampNativeMinY - nativeMinY * rootS;
        }
      });
    }

    updateModelDimensionLabels(root, { syncScale });

    /* 하이라이트 */
    applyHighlight();

    if (recenter) recenterCameraTo(root);
  }

  /* ===== Highlight 적용 ===== */
  function applyHighlight() {
    const none = !selectedPart;
    const keys = Object.keys(partsRef.current);

    for (const key of keys) {
      for (const mesh of partsRef.current[key]) {
        const m = mesh.material;
        if (!m) continue;
        m.transparent = true;

        if (none) {
          m.opacity = 1.0;
          m.emissive?.setHex?.(0x000000);
          m.emissiveIntensity = 0;
          mesh.renderOrder = 0;
        } else if (key === selectedPart) {
          m.opacity = 1.0;
          m.emissive?.setHex?.(0x222222);
          m.emissiveIntensity = 0.6;
          mesh.renderOrder = 1;
        } else {
          m.opacity = 0.25;
          m.emissive?.setHex?.(0x000000);
          m.emissiveIntensity = 0;
          mesh.renderOrder = 0;
        }
        m.needsUpdate = true;
      }
    }
  }

  // SVG 업로드 → 항상 카메라 리센터
  useEffect(() => {
    if (!svgText) return;
    if (modelRootRef.current) {
      modelRootRef.current.visible = false;
    }
    setRebuildLabel("generating");
    setIsRebuilding(true);
    requestAnimationFrame(() => {
      rebuildAll({ recenter: false });
      setIsRebuilding(false);
    });
  }, [svgText]);

  // 손잡이 STL이 첫 빌드보다 늦게 로드된 경우(AI 디자인 → 바로 커터 만들기) 재빌드
  useEffect(() => {
    if (!handleHoleReady || !svgText) return;
    setRebuildLabel("rebuilding");
    setIsRebuilding(true);
    requestAnimationFrame(() => {
      rebuildAll({ recenter: false });
      if (modelRootRef.current) {
        modelRootRef.current.visible = true;
      }
      setIsRebuilding(false);
    });
  }, [handleHoleReady]);

  useEffect(() => {
    if (!svgText) return;

    if (isScaleSyncUpdateRef.current) {
      if (modelRootRef.current) {
        modelRootRef.current.visible = true;
      }
      return;
    }

    const currentScale = Number(scaleMm);
    if (!Number.isFinite(currentScale) || currentScale <= 0) return;

    setRebuildLabel("rebuilding");
    setIsRebuilding(true);
    const timer = setTimeout(() => {
      rebuildAll({ recenter: false, applyFit: true });
      if (modelRootRef.current) {
        modelRootRef.current.visible = true;
      }
      setIsRebuilding(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [scaleMm, svgText]);

  useEffect(() => {
    if (!svgText) {
      prevScaleMmRef.current = null;
      isScaleSyncUpdateRef.current = false;
      return;
    }

    if (isScaleSyncUpdateRef.current) {
      const syncedScale = Number(scaleMm);
      if (Number.isFinite(syncedScale) && syncedScale > 0) {
        prevScaleMmRef.current = syncedScale;
      }
      isScaleSyncUpdateRef.current = false;
      return;
    }

    const currentScale = Number(scaleMm);
    if (!Number.isFinite(currentScale) || currentScale <= 0) return;

    const prevScale = prevScaleMmRef.current;
    if (!Number.isFinite(prevScale) || prevScale <= 0) {
      prevScaleMmRef.current = currentScale;
      if (!Number.isFinite(Number(dimensionBaseScaleMm))) {
        setDimensionBaseScaleMm(currentScale);
      }
      return;
    }

    if (currentScale === prevScale) return;

    prevScaleMmRef.current = currentScale;
  }, [scaleMm, svgText, dimensionBaseScaleMm]);

  // 값 변화 → 리센터 없음 (디바운스 300ms)
  useEffect(() => {
    if (!svgText) return;
    setRebuildLabel("rebuilding");
    setIsRebuilding(true);
    const timer = setTimeout(() => {
      rebuildAll({ recenter: false, applyFit: true });
      setIsRebuilding(false);
    }, 300);
    return () => clearTimeout(timer);
  }, [paramsKey]);

  // 모드 변경 시 리빌드만 수행 (스케일 재적용 없음)
  useEffect(() => {
    if (!svgText) return;
    setRebuildLabel("rebuilding");
    setIsRebuilding(true);
    requestAnimationFrame(() => {
      rebuildAll({ recenter: false, syncScale: true });
      setIsRebuilding(false);
    });
  }, [mode]);

  // 선택 파츠 하이라이트 즉시 반영
  useEffect(() => {
    applyHighlight();
  }, [selectedPart]);

  // 모드 변경 시 선택 초기화
  useEffect(() => {
    setSelectedPart(null);
  }, [mode]);

  useEffect(() => {
    if (sceneRef.current) {
      ensureGridCoordinateLabels(sceneRef.current);
    }
    if (modelRootRef.current) {
      updateModelDimensionLabels(modelRootRef.current);
    }
  }, [unit]);

  /* ==========================================
   🔥 editFileId 있으면 기존 파일 불러오기
   ========================================== */
  useEffect(() => {
    if (!editFileId) return;

    async function loadFileData() {
      const { data, error } = await supabase
        .from("user_files")
        .select("*")
        .eq("id", editFileId)
        .single();

      if (error) {
        console.error("불러오기 실패:", error);
        return;
      }

      if (data.svg_text) {
        setSvgText(data.svg_text);

        const blob = new Blob([data.svg_text], { type: "image/svg+xml" });
        const url = URL.createObjectURL(blob);

        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
        }

        previewUrlRef.current = url;
        setPreviewUrl(url);
      }

      if (data.file_name) setFileName(data.file_name.replace(".stl", ""));

      if (data.params) {
        const p = data.params;
        if (p.scaleMm) setScaleMm(p.scaleMm);
        if (p.knifeH) setKnifeH(p.knifeH);
        if (p.knifeT) setKnifeT(p.knifeT);
        if (p.baseH) setBaseH(p.baseH);
        if (p.baseT) setBaseT(p.baseT);
        if (p.stampBaseH) setStampBaseH(p.stampBaseH);
        if (p.stampTextH) setStampTextH(p.stampTextH);
        if (p.stampFitGap != null) setStampFitGap(p.stampFitGap);
        if (p.dimensionBaseScaleMm) {
          setDimensionBaseScaleMm(p.dimensionBaseScaleMm);
        } else if (p.scaleMm) {
          setDimensionBaseScaleMm(p.scaleMm);
        }
        if (p.mode) setMode(p.mode);
      }
    }

    loadFileData();
  }, [editFileId]);

  function stripTimestamp(name) {
    // _YYYYMMDD_HHMMSS 패턴 제거
    return name.replace(/_\d{8}_\d{6}$/, "");
  }

  /* ===== 저장 시 각 파트를 "깨끗한 워터타이트 솔리드"로 정리해 내보내기 =====
     브라우저 CSG 불리언은 복잡한 트레이싱 그림에서 깨진 면을 만들어 사용하지 않는다.
     대신 유일한 진짜 결함이던 "비매니폴드 2층 바닥"을 온전한 솔리드로 교체하고,
     나머지 파트(도장면·커터 벽/테두리·손잡이)는 각자 워터타이트 메쉬로 개별 유지한다.
     STL 하나를 슬라이서가 한 덩어리로 슬라이스하므로 프린트엔 문제 없다. */

  /* ===== 내보내기용 파트 합치기 (손잡이 / 스탬프 / 커터 3덩어리) =====
     슬라이서의 "객체 분리"는 정점·에지를 공유하는 덩어리 단위로 쪼갠다.
     화면용 메쉬는 파트별로 따로라 그대로 내보내면 7덩어리로 갈라지므로,
     내보낼 때만 3개 조립체로 합치고 맞닿는 링을 용접한다. (화면은 그대로) */

  function geometryPositionsOnly(mesh) {
    const src = mesh.geometry;
    const g = src.index ? src.toNonIndexed() : src.clone();
    const out = new THREE.BufferGeometry();
    out.setAttribute("position", g.getAttribute("position").clone());
    out.applyMatrix4(mesh.matrixWorld);
    if (g !== src) g.dispose();
    return out;
  }

  /* three.js mergeVertices는 좌표를 격자에 반올림해 묶는다. 그래서 격자 경계를 사이에
     둔 "거의 같은 점"(Clipper 오프셋이 만드는 1e-5mm 차이)은 끝내 안 붙고 남는다.
     그렇게 갈라진 정점이 T-junction을 만들고, 정점을 좌표 그대로 대조하는 슬라이서가
     그걸 열린 에지(non-manifold edge)로 잡는다.
     인접 셀까지 훑어 실제 거리로 묶은 뒤 대표점 좌표를 그대로 복사해,
     이어서 도는 mergeVertices가 확실히 하나로 합치게 한다. */
  function snapNearDuplicates(geo, tol = SNAP_TOLERANCE_MM) {
    const pos = geo.getAttribute("position");
    const n = pos.count;
    const parent = new Int32Array(n);
    for (let i = 0; i < n; i++) parent[i] = i;
    const find = (x) => {
      let r = x;
      while (parent[r] !== r) r = parent[r];
      while (parent[x] !== r) {
        const p = parent[x];
        parent[x] = r;
        x = p;
      }
      return r;
    };

    const grid = new Map();
    const tol2 = tol * tol;
    for (let i = 0; i < n; i++) {
      const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
      const cx = Math.floor(x / tol), cy = Math.floor(y / tol), cz = Math.floor(z / tol);
      for (let dx = -1; dx <= 1; dx++) {
        for (let dy = -1; dy <= 1; dy++) {
          for (let dz = -1; dz <= 1; dz++) {
            const bucket = grid.get(`${cx + dx}|${cy + dy}|${cz + dz}`);
            if (!bucket) continue;
            for (const j of bucket) {
              const ddx = pos.getX(j) - x, ddy = pos.getY(j) - y, ddz = pos.getZ(j) - z;
              if (ddx * ddx + ddy * ddy + ddz * ddz > tol2) continue;
              const a = find(i), b = find(j);
              if (a !== b) parent[b] = a;
            }
          }
        }
      }
      const k = `${cx}|${cy}|${cz}`;
      const b = grid.get(k);
      if (b) b.push(i);
      else grid.set(k, [i]);
    }

    for (let i = 0; i < n; i++) {
      const r = find(i);
      if (r !== i) pos.setXYZ(i, pos.getX(r), pos.getY(r), pos.getZ(r));
    }
    pos.needsUpdate = true;
    return geo;
  }

  function weldMeshes(meshes) {
    if (!meshes.length) return null;
    const geos = meshes.map(geometryPositionsOnly);
    const merged =
      geos.length === 1 ? geos[0] : BufferGeometryUtils.mergeGeometries(geos, false);
    if (!merged) {
      geos.forEach((g) => g.dispose());
      return null;
    }

    const welded = BufferGeometryUtils.mergeVertices(snapNearDuplicates(merged), WELD_TOLERANCE_MM);
    welded.computeVertexNormals();
    geos.forEach((g) => g.dispose());
    if (merged !== geos[0]) merged.dispose();
    return welded;
  }

  /* 재구성한 지오메트리가 원본 파트들과 같은 크기인지 확인 (아니면 폐기하고 폴백) */
  function matchesOriginalSize(geo, meshes) {
    const rebuilt = new THREE.Box3().setFromBufferAttribute(geo.getAttribute("position"));
    const original = new THREE.Box3();
    for (const m of meshes) original.union(new THREE.Box3().setFromObject(m));
    const rs = rebuilt.getSize(new THREE.Vector3());
    const os = original.getSize(new THREE.Vector3());
    const ok =
      Math.abs(rs.x - os.x) <= 0.2 && Math.abs(rs.y - os.y) <= 0.2 && Math.abs(rs.z - os.z) <= 0.2;
    if (!ok) console.warn("[STL] 재구성 결과 크기 불일치 → 기존 방식으로 내보냄", rs, os);
    return ok;
  }

  /* 재구성 결과가 닫힌 솔리드가 아니면(열린 면·비매니폴드) 폐기하고 기존 방식으로 폴백.
     슬라이서가 "non-manifold edges"로 잡는 건 대부분 여기서 새는 면이다. */
  function isWatertight(geo, partName) {
    const r = inspectMesh(geo);
    if (r.boundary < 0) return true; // 너무 커서 측정을 생략한 경우는 기존 동작 유지
    const ok = r.boundary === 0 && r.nonManifold === 0;
    if (!ok) console.warn(`[STL] ${partName} 재구성 결과가 닫히지 않음 → 기존 방식으로 내보냄`, r);
    return ok;
  }

  function buildWeldedCutterMesh(wallMesh, cutterMeshes) {
    const params = wallMesh?.userData?.cutter;
    if (!params) return null;

    const geo = buildWeldedCutterGeometry(params);
    if (!geo) return null;
    geo.applyMatrix4(wallMesh.matrixWorld);

    if (!matchesOriginalSize(geo, cutterMeshes)) {
      geo.dispose();
      return null;
    }

    // normal이 붙어 있으면 mergeVertices가 normal까지 해시에 넣어 정점이 거의 안 합쳐진다
    geo.deleteAttribute("normal");
    const welded = BufferGeometryUtils.mergeVertices(snapNearDuplicates(geo), WELD_TOLERANCE_MM);
    welded.computeVertexNormals();
    geo.dispose();
    if (!isWatertight(welded, "커터")) {
      welded.dispose();
      return null;
    }
    return welded;
  }

  /* position만 남긴 비인덱스 사본 (서로 다른 파트를 합치기 전에 속성을 맞춘다) */
  function positionsOf(geo) {
    const src = geo.index ? geo.toNonIndexed() : geo;
    const out = new THREE.BufferGeometry();
    out.setAttribute("position", src.getAttribute("position").clone());
    if (src !== geo) src.dispose();
    return out;
  }

  /* 소켓 바닥을 스탬프 바닥에 맞춘다. 어긋나면 링이 안 겹쳐 스탬프와 이어지지 않고,
     소켓만 베드에 닿아 스탬프 바닥 첫 레이어가 뜬다.
     지금은 handleHole.js에서 이미 같은 높이로 놓지만, 어긋나도 여기서 잡아준다. */
  function alignPlugBottom(plugGeo, baseMesh) {
    if (!baseMesh) return;
    plugGeo.computeBoundingBox();
    const dy = new THREE.Box3().setFromObject(baseMesh).min.y - plugGeo.boundingBox.min.y;
    if (dy > 0 && dy < 1) plugGeo.translate(0, dy, 0); // 표시용 미세 오프셋만 보정
  }

  function buildWeldedStampMesh(baseMesh, faceMeshes, plugMeshes = []) {
    if (!baseMesh) return null;
    const { baseShape, baseDepth, curveSegments, pocket } = baseMesh.userData || {};
    const faceShapes = faceMeshes.map((m) => m.userData?.faceShape).filter(Boolean);
    if (!baseShape || !baseDepth) return null;
    if (faceMeshes.length && faceShapes.length !== faceMeshes.length) return null;

    /* 소켓이 포켓을 정확히 채울 때만(ringPts 존재) 포켓 벽을 소켓 바깥면에 맡긴다.
       양쪽 다 만들면 같은 면이 두 겹이 되어 비매니폴드가 되므로 한쪽씩 걷어낸다. */
    const socketWall = plugMeshes.length > 0 && pocket?.ringPts?.length >= 3;

    const geo = buildWeldedStampGeometry({
      baseShape,
      baseDepth,
      curveSegments,
      pocket,
      socketWall,
      faceShapes,
      faceDepth: faceMeshes[0]?.userData?.faceDepth || 0,
    });
    if (!geo) return null;

    geo.applyMatrix4(baseMesh.matrixWorld);

    if (!matchesOriginalSize(geo, [baseMesh, ...faceMeshes])) {
      geo.dispose();
      return null;
    }

    const parts = [positionsOf(geo)];
    geo.dispose();

    if (plugMeshes.length) {
      const plug = weldMeshes(plugMeshes);
      if (!plug) {
        parts.forEach((p) => p.dispose());
        return null;
      }
      alignPlugBottom(plug, baseMesh);
      // 벽을 소켓에 맡긴 경우엔 스탬프 재료에 파묻힐 바깥 원통·윗면을 걷어낸다
      const part = socketWall ? stripSocketOuterShell(plug) : positionsOf(plug);
      plug.dispose();
      if (!part) {
        parts.forEach((p) => p.dispose());
        return null;
      }
      parts.push(part);
    }

    const merged =
      parts.length === 1 ? parts[0] : BufferGeometryUtils.mergeGeometries(parts, false);
    if (!merged) {
      parts.forEach((p) => p.dispose());
      return null;
    }

    const welded = BufferGeometryUtils.mergeVertices(snapNearDuplicates(merged), WELD_TOLERANCE_MM);
    welded.computeVertexNormals();
    parts.forEach((p) => p !== merged && p.dispose());
    if (merged !== parts[0]) merged.dispose();
    if (!isWatertight(welded, "스탬프")) {
      welded.dispose();
      return null;
    }
    return welded;
  }

  /* 슬라이서가 보는 메쉬 상태: 덩어리 수 / 구멍(경계 에지) / non-manifold 에지 */
  function inspectMesh(geo) {
    const chunks = countConnectedComponents(geo);
    const pos = geo.getAttribute("position");
    const idx = geo.index;
    const triCount = idx ? idx.count / 3 : pos.count / 3;
    if (triCount > 400000) return { chunks, boundary: -1, nonManifold: -1, degenerate: -1 };

    const ids = vertexIds(geo);
    const edges = new Map();
    let degenerate = 0;
    for (let t = 0; t < triCount; t++) {
      const a = ids[t * 3];
      const b = ids[t * 3 + 1];
      const c = ids[t * 3 + 2];
      if (a === b || b === c || a === c) {
        degenerate++;
        continue;
      }
      for (const [u, v] of [[a, b], [b, c], [c, a]]) {
        const k = u < v ? `${u}_${v}` : `${v}_${u}`;
        edges.set(k, (edges.get(k) || 0) + 1);
      }
    }
    let boundary = 0;
    let nonManifold = 0;
    for (const c of edges.values()) {
      if (c === 1) boundary++;
      else if (c > 2) nonManifold++;
    }
    return { chunks, boundary, nonManifold, degenerate };
  }

  /* 슬라이서는 인덱스가 아니라 "좌표"로 정점을 붙이므로 똑같이 좌표로 판정한다.
     인덱스를 믿으면 안 된다 — mergeVertices는 normal 같은 다른 속성까지 해시에 넣기 때문에
     같은 자리의 정점이 인덱스상으로는 갈라져 있을 수 있다. */
  function vertexIds(geo) {
    const pos = geo.getAttribute("position");
    const idx = geo.index;
    const count = idx ? idx.count : pos.count;
    const map = new Map();
    const ids = new Int32Array(count);
    for (let i = 0; i < count; i++) {
      const v = idx ? idx.getX(i) : i;
      const key = `${pos.getX(v)},${pos.getY(v)},${pos.getZ(v)}`;
      let id = map.get(key);
      if (id === undefined) {
        id = map.size;
        map.set(key, id);
      }
      ids[i] = id;
    }
    return ids;
  }

  /* 슬라이서가 "객체 분리"로 쪼갤 덩어리 수 = 정점 공유 기준 연결 요소 수 */
  function countConnectedComponents(geo) {
    const pos = geo.getAttribute("position");
    const idx = geo.index;
    const triCount = idx ? idx.count / 3 : pos.count / 3;
    if (triCount > 400000) return -1; // 너무 크면 측정 생략

    const ids = vertexIds(geo);

    let max = 0;
    for (let i = 0; i < ids.length; i++) if (ids[i] > max) max = ids[i];
    const parent = new Int32Array(max + 1);
    for (let i = 0; i <= max; i++) parent[i] = i;
    const find = (x) => {
      let r = x;
      while (parent[r] !== r) r = parent[r];
      while (parent[x] !== r) {
        const n = parent[x];
        parent[x] = r;
        x = n;
      }
      return r;
    };
    const union = (a, b) => {
      const ra = find(a);
      const rb = find(b);
      if (ra !== rb) parent[rb] = ra;
    };

    const used = new Set();
    for (let t = 0; t < triCount; t++) {
      const a = ids[t * 3];
      const b = ids[t * 3 + 1];
      const c = ids[t * 3 + 2];
      union(a, b);
      union(a, c);
      used.add(a);
      used.add(b);
      used.add(c);
    }

    const roots = new Set();
    for (const v of used) roots.add(find(v));
    return roots.size;
  }

  function buildExportAssemblies(source) {
    if (!source) return null;
    source.updateMatrixWorld(true);

    const cutterMeshes = [];
    const handleMeshes = [];
    const faceMeshes = [];
    const plugMeshes = [];
    const otherStamp = [];
    let baseMesh = null;
    let wallMesh = null;

    source.traverse((o) => {
      if (!o.isMesh || !o.geometry) return;
      const t = o.userData?.partType;
      // 손잡이 결합 소켓 — 속이 빈 컵이라 안쪽 홈에 손잡이 돌기가 물린다.
      // 스탬프 바닥 포켓(같은 반지름)에 그대로 들어가므로 함께 인쇄한다.
      if (t === "stampBasePlug") {
        plugMeshes.push(o);
        return;
      }
      if (t === "cutterWall" || t === "cutterBase") {
        cutterMeshes.push(o);
        if (t === "cutterWall") wallMesh = o;
      } else if (t === "stampHandle") handleMeshes.push(o);
      else if (t === "stampFace") faceMeshes.push(o);
      else if (t === "stampBase") baseMesh = o;
      else otherStamp.push(o);
    });

    const group = new THREE.Group();
    const mat = new THREE.MeshBasicMaterial();

    if (cutterMeshes.length) {
      const cutterGeo =
        buildWeldedCutterMesh(wallMesh, cutterMeshes) || weldMeshes(cutterMeshes);
      if (cutterGeo) group.add(new THREE.Mesh(cutterGeo, mat));
    }

    const stampMeshes = [...(baseMesh ? [baseMesh] : []), ...faceMeshes, ...otherStamp];
    if (stampMeshes.length) {
      const stampGeo =
        (otherStamp.length ? null : buildWeldedStampMesh(baseMesh, faceMeshes, plugMeshes)) ||
        weldMeshes([...stampMeshes, ...plugMeshes]);
      if (stampGeo) group.add(new THREE.Mesh(stampGeo, mat));
    }

    const handleGeo = weldMeshes(handleMeshes);
    if (handleGeo) group.add(new THREE.Mesh(handleGeo, mat));

    return group.children.length ? group : null;
  }

  /* ===== STL 서버 업로드 & DB 저장 & 다운로드 ===== */
  async function exportSTL() {
    if (downloading) return;
    setStorageExceeded(false);  // 🔥 중복 클릭 방지
    setDownloading(true);      // 🔥 로딩 시작
    let exportTarget = null;

    if (mode === "cutter") {
      exportTarget = cutterGroupRef.current;
    } else if (mode === "stamp") {
      exportTarget = plainGroupRef.current;
    } else {
      exportTarget = modelRootRef.current; // both
    }

    const root = exportTarget;
    const renderer = rendererRef.current;

    if (!root || !renderer) {
      showAlert(t("cutter.noModel"));
      setDownloading(false);
      return;
    }

    rebuildAll({ recenter: false });

    try {
      /* STL 다운로드는 횟수 제한 없음 (2026-10-15 김현비, 요금제 폐지).
         비회원 다운로드는 통계용으로만 서버에 기록한다 — 실패해도 다운로드는 막지 않는다. */
      if (!user?.id) {
        const API_BASE = process.env.REACT_APP_API_BASE_URL;
        fetch(`${API_BASE}/guest-stl-check`, { method: "POST" }).catch(() => {});
      }

      /* 1️⃣ STL 생성 */
      const exporter = new STLExporter();
      const exportWrapper = new THREE.Group();

      // 손잡이 / 스탬프 / 커터 3덩어리로 합쳐서 내보낸다 (실패 시 기존 방식)
      exportWrapper.add(buildExportAssemblies(exportTarget) || exportTarget.clone(true));
      // three.js(Y-up) → 슬라이서(Z-up): 눕혀서 바닥이 베드에 닿도록 회전.
      // Y 180°는 화면 방향 보정 — 카메라(FIXED_VIEW_DIR)가 모델 -Z 쪽에서 보고 있어
      // 화면 오른쪽이 월드 -X다. 이걸 안 맞추면 슬라이서에서 Z축으로 180° 돌아 보인다.
      exportWrapper.rotation.set(Math.PI / 2, Math.PI, 0);
      exportWrapper.updateMatrixWorld(true);

      const exportBox = new THREE.Box3().setFromObject(exportWrapper);
      if (!exportBox.isEmpty()) {
        exportWrapper.position.z -= exportBox.min.z;
        exportWrapper.updateMatrixWorld(true);
      }

      // 🔍 [임시 진단] 삼각형 수 / 슬라이서가 쪼갤 덩어리 수 / 예상 STL 용량
      try {
        let triTotal = 0;
        const reports = [];
        exportWrapper.traverse((obj) => {
          if (!obj.isMesh || !obj.geometry) return;
          const g = obj.geometry;
          triTotal += g.index ? g.index.count / 3 : g.attributes.position.count / 3;
          reports.push(inspectMesh(g));
        });
        const bytes = 84 + 50 * triTotal;
        const sum = (k) => reports.reduce((a, r) => a + r[k], 0);
        console.log(
          `[STL 진단] 덩어리 ${sum("chunks")}개 · 구멍(경계에지) ${sum("boundary")} · non-manifold 에지 ${sum("nonManifold")} · 퇴화삼각형 ${sum("degenerate")}`,
          reports
        );
        console.log(
          `[STL 진단] 총 삼각형: ${triTotal.toLocaleString()}개 · 예상 용량: ${(bytes / 1024 / 1024).toFixed(2)} MB · curveSegments=${curveSegments} (eff=${effectiveCurveSegments}, cutter=${cutterCurveSegments}) · mode=${mode}`
        );
      } catch (e) {
        console.warn("[STL 진단] 측정 실패:", e);
      }

      const stlArrayBuffer = exporter.parse(exportWrapper, { binary: true });

      // 🔥 파일명 생성 (날짜+시간)
      const now = new Date();
      const pad = (n) => String(n).padStart(2, "0");
      const timestamp =
        now.getFullYear() +
        pad(now.getMonth() + 1) +
        pad(now.getDate()) +
        "_" +
        pad(now.getHours()) +
        pad(now.getMinutes()) +
        pad(now.getSeconds());

      const baseNameRaw = stripTimestamp(fileName || "cookie-cutter");

      const safeBaseName = baseNameRaw
        .replace(/\.svg$/i, "")
        .replace(/[^a-zA-Z0-9_-]/g, "_");

      const finalBaseName = `${safeBaseName}_${timestamp}`;

      const stlBlob = new Blob([stlArrayBuffer], { type: "model/stl" });

      /* ============================
     🔓 비로그인: 로컬 STL 다운로드만
      ============================ */
      if (!user?.id) {
        const url = URL.createObjectURL(stlBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${finalBaseName}.stl`;
        document.body.appendChild(a);
        a.click();
        trackAdActivity("stl_download");
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        setDownloadMsg(t("cutter.downloadStarted"));
        setTimeout(() => setDownloadMsg(""), 3000);
        setDownloading(false); // 🔴 추가
        return;
      }

      if (!saveToMyPage) {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        const url = URL.createObjectURL(stlBlob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${finalBaseName}.stl`;
        document.body.appendChild(a);
        a.click(); // ✅ 다운로드 실제 발생
        trackAdActivity("stl_download");
        document.body.removeChild(a);
        URL.revokeObjectURL(url);

        // ✅ 반드시 a.click() "직후"
        await supabase.from("download_logs").insert({
          user_id: session.user.id,
        });

        setDownloadMsg(t("cutter.downloadStarted"));
        setTimeout(() => setDownloadMsg(""), 3000);
        return;
      }

      // 저장 개수 확인 — 휴지통 제외 STL 10개까지 (config/features.js)
      const { count: savedCount, error: usageErr } = await supabase
        .from("user_files")
        .select("id", { count: "exact", head: true })
        .eq("user_id", user.id)
        .eq("is_deleted", false);

      if (usageErr) {
        showAlert(t("cutter.loadStorageFailed"));
        setDownloading(false);
        return;
      }

      if (user.role !== "admin" && (savedCount || 0) >= FILE_SAVE_LIMIT) {
        setStorageExceeded(true);
        showAlert(t("cutter.saveLimitReached", { limit: FILE_SAVE_LIMIT }));
        setDownloading(false);
        return;
      }

      /* 2️⃣ STL 서버 업로드 */
      const formData = new FormData();
      formData.append("file", stlBlob, `${finalBaseName}.stl`);
      formData.append("user_id", user.id);
      formData.append("base_name", finalBaseName);

      // ✅ 여기 추가
      const API_BASE = process.env.REACT_APP_API_BASE_URL;
      console.log("API_BASE =", API_BASE);

      // ❗ fetch는 이 아래
      const res = await fetch(`${API_BASE}/upload_stl`, {
        method: "POST",
        body: formData,
      });


      if (!res.ok) {
        showAlert(t("cutter.stlProcessingError"));
        return;
      }

      const data = await res.json();

      if (!data?.url || !data?.storage_path) {
        showAlert(t("cutter.stlUploadFailed"));
        return;
      }

      /* 3️⃣ 썸네일 생성 (정사각형 크롭) */
      const scene = sceneRef.current;
      const camera = cameraRef.current;

      renderer.render(scene, camera);

      const canvas = renderer.domElement;
      const size = Math.min(canvas.width, canvas.height);
      const offX = Math.floor((canvas.width - size) / 2);
      const offY = Math.floor((canvas.height - size) / 2);

      const cropCanvas = document.createElement("canvas");
      cropCanvas.width = 512;
      cropCanvas.height = 512;
      const ctx = cropCanvas.getContext("2d");
      ctx.drawImage(canvas, offX, offY, size, size, 0, 0, 512, 512);

      const thumbBlob = await new Promise((resolve) =>
        cropCanvas.toBlob(resolve, "image/png")
      );

      if (!thumbBlob) {
        showAlert(t("cutter.thumbnailGenerateFailed"));
        return;
      }

      /* 4️⃣ 썸네일 업로드 */
      const thumbPath = `thumbnails/${user.id}/${finalBaseName}_thumb.png`;

      const { error: thumbErr } = await supabase.storage
        .from("user-files")
        .upload(thumbPath, thumbBlob, {
          contentType: "image/png",
          upsert: true,
        });

      if (thumbErr) {
        console.error(thumbErr);
        showAlert(t("cutter.thumbnailUploadFailed"));
        return;
      }

      const { data: thumbUrlData } = supabase.storage
        .from("user-files")
        .getPublicUrl(thumbPath);

      const thumbnailUrl = thumbUrlData.publicUrl;

      /* 5️⃣ DB 저장 */
      const hBox = boxExcludingHandle(root);
      const hSize = new THREE.Vector3();
      if (!hBox.isEmpty()) hBox.getSize(hSize);
      const modelSizeForDb = hBox.isEmpty() ? null : { x: hSize.x, y: hSize.y, z: hSize.z };

      const { error: dbErr } = await supabase.from("user_files").insert({
        user_id: user.id,
        file_name: `${finalBaseName}.stl`,
        file_url: data.url,
        storage_path: data.storage_path,
        thumbnail_url: thumbnailUrl,
        svg_text: svgText,
        size_mb: Math.ceil(stlBlob.size / (1024 * 1024)),
        params: {
          knifeH,
          knifeT,
          baseH,
          baseT,
          stampBaseH,
          stampTextH,
          stampFitGap,
          scaleMm,
          keepDimensionRatio,
          dimensionBaseScaleMm,
          mode,
          modelSize: modelSizeForDb,
        },
      });

      if (dbErr) {
        console.error("DB 저장 실패:", dbErr);
        showAlert(t("cutter.dbSaveFailed"));
        return;
      }

      /* 6️⃣ 다운로드 */
      const {
        data: { session },
      } = await supabase.auth.getSession();

      const a = document.createElement("a");
      a.href = data.url;
      a.download = `${finalBaseName}.stl`;
      document.body.appendChild(a);
      a.click();          // ✅ 딱 한 번
      trackAdActivity("stl_download");
      document.body.removeChild(a);

      await supabase.from("download_logs").insert({
        user_id: session.user.id,
      });

      setTodayDownloadCount((prev) => prev + 1);


      setDownloadMsg(t("cutter.downloadStarted"));
      setTimeout(() => setDownloadMsg(""), 3000);
    }

    catch (err) {
      console.error(err);
      showAlert(t("cutter.stlProcessingError"));
    } finally {
      setDownloading(false);     // 🔥 로딩 종료
    }
  }

  /* ===== 반환(UI 그대로) ===== */
  /* 마이페이지 STL 보관 개수 (n / 10) */
  useEffect(() => {
    if (!user?.id) { setSavedFileCount(null); return; }
    countSaved(user.id).then((c) => setSavedFileCount(c.files)).catch(() => {});
  }, [user?.id, downloading]);

  /* 지금 화면의 모델로 STL Blob 을 만든다 (주문·갤러리 올리기 공용, exportSTL 과 같은 방향 보정) */
  function buildCurrentStlBlob() {
    const exportTarget = mode === "cutter" ? cutterGroupRef.current
      : mode === "stamp" ? plainGroupRef.current
      : modelRootRef.current;
    if (!exportTarget) return null;

    const exporter = new STLExporter();
    const wrapper = new THREE.Group();
    wrapper.add(buildExportAssemblies(exportTarget) || exportTarget.clone(true));
    // three.js(Y-up) → 슬라이서(Z-up) + 화면 방향 보정 (exportSTL과 동일)
    wrapper.rotation.set(Math.PI / 2, Math.PI, 0);
    wrapper.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(wrapper);
    if (!box.isEmpty()) {
      wrapper.position.z -= box.min.z;
      wrapper.updateMatrixWorld(true);
    }
    const buf = exporter.parse(wrapper, { binary: true });
    return new Blob([buf], { type: "model/stl" });
  }

  /* STL 을 만들어 IndexedDB 에 넣고 3D프린팅 주문 페이지로 이동 */
  function goOrderWithCurrentStl() {
    if (!svgText) return;
    // STL 생성 후 IndexedDB에 저장하고 주문 페이지로 이동
    const blob = buildCurrentStlBlob();
    if (!blob) return;

    saveOrderStl(blob, `${fileName || "cookistory"}.stl`)
      .then(() => history.push("/order"))
      .catch(() => showAlert("STL 저장 실패. 다시 시도해주세요."));
  }

  /* 갤러리에 올리기 (회원 전용, 2026-10-16 김현비): STL + 원본 SVG 를 넘기고 올리기 화면으로 */
  function goGalleryWithCurrentStl() {
    if (!svgText || !user) return;
    const stlBlob = buildCurrentStlBlob();
    if (!stlBlob) return;

    const base = String(fileName || "")
      .replace(/\.(svg|png|stl)$/i, "")
      .replace(/[^a-zA-Z0-9_-]/g, "_")
      .replace(/^_+|_+$/g, "");

    saveGalleryHandoff({ stlBlob, svgText, baseName: base || "cookie-cutter" })
      .then(() => history.push("/gallery/write?from=cutter"))
      .catch(() => showAlert("STL 저장 실패. 다시 시도해주세요."));
  }

  /* 갤러리 "다시 만들기" (?gallery=<id>): 그 글의 원본 SVG 를 직접 올린 것처럼 불러온다 */
  useEffect(() => {
    if (!galleryParamId) return;
    let cancelled = false;

    async function loadFromGallery() {
      const { data, error } = await supabase
        .from("gallery")
        .select("id, title, svg_url")
        .eq("id", galleryParamId)
        .single();
      if (cancelled || error || !data?.svg_url) {
        if (error) console.error("갤러리 그림 불러오기 실패:", error);
        return;
      }
      try {
        const res = await fetch(data.svg_url);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        const base = String(data.title || "cookie-cutter").replace(/[^a-zA-Z0-9_-]/g, "_") || "cookie-cutter";
        // 옛 글은 원본 그림이 PNG 인 것도 있다 → PNG 면 직접 올린 PNG 처럼 변환 경로로
        const isPng = /png/i.test(blob.type) || /\.png(\?|$)/i.test(data.svg_url);
        handleSvgFile(isPng
          ? new File([blob], `${base}.png`, { type: "image/png" })
          : new File([await blob.text()], `${base}.svg`, { type: "image/svg+xml" }));
      } catch (err) {
        console.error("갤러리 그림 불러오기 실패:", err);
      }
    }

    loadFromGallery();
    return () => { cancelled = true; };
  }, [galleryParamId]);

  return (
    <div className="app-wrapper">
      {downloadMsg && (
        <div className="download-overlay">
          <div className="download-modal">
            {downloadMsg}
          </div>
        </div>
      )}

      {downloading && (
        <div className="download-overlay">
          <div className="download-modal">
            {t("cutter.generatingStl")}
          </div>
        </div>
      )}

      {isConvertingSvg && (
        <div className="download-overlay">
          <div className="download-modal">
            {t("cutter.convertingSvg")}
          </div>
        </div>
      )}


      <div className="cc-container ck-cutter">
        {/* === 사이드바 (2026-10-15 리뉴얼: ① 그림 → ② 구성 → ③ 크기 → 세부 치수, 아래 버튼 고정) === */}
        <aside className="cc-sidebar ckc-panel" style={window.innerWidth > 900 ? { width: sidebarW } : undefined}>
          <div className="ckc-scroll">
            <div className="ckc-title">{t("cutter.brandTitle")}</div>
            <div className="ckc-sub">{t("cutterUi.subtitle")}</div>

            {/* ① 그림 */}
            <section
              className={`ckc-sec ${isDragging ? "drag-active" : ""}`}
              onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
              onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
              onDrop={(e) => {
                e.preventDefault();
                setIsDragging(false);
                const file = e.dataTransfer?.files?.[0];
                handleSvgFile(file);
              }}
            >
              <div className="ckc-sec-h"><span className="ckc-n">1</span><b>{t("cutterUi.secImage")}</b><span className="ckc-hint">PNG · SVG</span><button type="button" className="ckc-info" title={t("cutter.svgGuideNote")} onClick={() => setSvgGuideOpen(true)}>?</button></div>
              <input id="svgFile" type="file" accept=".svg,.png" onChange={onUpload} hidden />
              {fileName ? (
                <div className="ckc-file">
                  <div className="ckc-th">{previewUrl && <img src={previewUrl} alt={t("cutter.svgPreviewAlt")} />}</div>
                  <div className="ckc-meta">
                    <b title={fileName}>{fileName}</b>
                    {svgText && <div className="ckc-ok">✓ {t("cutterUi.shapeReady")}</div>}
                  </div>
                  <div className="ckc-acts">
                    <label htmlFor="svgFile" className="ckc-ib">{t("cutterUi.change")}</label>
                    <button type="button" className="ckc-ib ghost" onClick={clearSvg}>{t("cutter.clear")}</button>
                  </div>
                </div>
              ) : (
                <label htmlFor="svgFile" className="ckc-file empty">
                  {t("cutterUi.dropOr")} <b>{t("cutter.selectSvg")}</b>
                </label>
              )}
              {/* 사이드바 덜어내기 (2026-10-15): SVG 안내는 ? 버튼으로, AI 도안 링크는 그림 올리기 전에만 */}
              {!fileName && (
                <div className="ckc-tip">
                  {t("cutterUi.noImage")}{" "}
                  <span className="ckc-link" onClick={() => history.push("/design")}>{t("cutterUi.aiDesign")}</span>
                </div>
              )}
            </section>

            <div className={svgText ? "" : "ckc-locked"}>
              {/* ② 구성 */}
              <section className="ckc-sec">
                <div className="ckc-sec-h"><span className="ckc-n">2</span><b>{t("cutterUi.secMode")}</b></div>
                <div className="ckc-seg">
                  {[
                    ["both", t("cutterUi.modeBoth")],
                    ["cutter", t("cutterUi.modeCutterOnly")],
                    ["stamp", t("cutterUi.modeStampOnly")],
                  ].map(([v, label]) => (
                    <button type="button" key={v} className={mode === v ? "on" : ""} onClick={() => setMode(v)}>
                      <ModeIcon kind={v} />
                      {label}
                    </button>
                  ))}
                </div>
              </section>

              {/* ③ 크기 */}
              <section className="ckc-sec">
                <div className="ckc-sec-h">
                  <span className="ckc-n">3</span><b>{t("cutterUi.secSize")}</b>
                  <span className="ckc-unit">
                    {["mm", "in"].map((u) => (
                      <button type="button" key={u} className={unit === u ? "on" : ""} onClick={() => setUnit(u)}>{u}</button>
                    ))}
                  </span>
                </div>
                <div
                  className="ckc-size-row"
                  data-focus={selectedPart === "scale" ? "on" : "off"}
                  onClick={() => setSelectedPart("scale")}
                >
                  <input
                    className="ckc-size-input"
                    style={{ width: `${Math.max(1, String(toDisplayUnit(shownScaleMm) ?? "").length) * 0.6 + 0.1}em` }}
                    type="number"
                    min={scaleMinByUnit}
                    max={scaleMaxByUnit}
                    step={unit === "in" ? "0.001" : "0.1"}
                    value={toDisplayUnit(shownScaleMm)}
                    onChange={(e) => {
                      const raw = fromDisplayUnit(e.target.value);
                      const v = Number(raw);
                      if (Number.isFinite(v) && v > SCALE_MAX_MM) setScaleMm(SCALE_MAX_MM);
                      else setScaleMm(raw);
                    }}
                    onBlur={handleScaleBlur}
                  />
                  <span className="ckc-u">{unitLabel}</span>
                  {/* 가로×세로는 3D 화면 아래에 이미 나오므로 여기선 안내만 */}
                  <span className="ckc-wh">{t("cutterUi.longSideHelp")}</span>
                </div>
                {/* 크기 슬라이더는 유지 (2026-10-15 김현비: 세밀 조절 필요) */}
                <input
                  className="ckc-range"
                  type="range"
                  min={SCALE_MIN_MM}
                  max={SCALE_MAX_MM}
                  step="1"
                  value={Number(shownScaleMm) || SCALE_MIN_MM}
                  style={{ "--p": `${((Math.min(SCALE_MAX_MM, Math.max(SCALE_MIN_MM, Number(shownScaleMm) || SCALE_MIN_MM)) - SCALE_MIN_MM) / (SCALE_MAX_MM - SCALE_MIN_MM)) * 100}%` }}
                  onChange={(e) => setScaleMm(Number(e.target.value))}
                />
                <div className="ckc-range-l"><span>{scaleMinByUnit}{unitLabel}</span><span>{scaleMaxByUnit}{unitLabel}</span></div>
                <div className="ckc-presets">
                  {SIZE_PRESETS.map(([key, mm]) => (
                    <button type="button" key={key} className={Math.abs(Number(shownScaleMm) - mm) < 0.5 ? "on" : ""} onClick={() => setScaleMm(mm)}>
                      {t(`cutterUi.preset.${key}`)}<small>{toDisplayUnit(mm)}{unitLabel}</small>
                    </button>
                  ))}
                </div>
              </section>

              {/* 세부 치수 (기본: 접힘) */}
              <section className={`ckc-sec ckc-adv ${advOpen ? "open" : ""}`} onClick={advOpen ? undefined : () => setAdvOpen(true)}>
                <button type="button" className="ckc-adv-h" onClick={() => setAdvOpen((v) => !v)}>
                  <span className="ckc-n soft">+</span><b>{t("cutterUi.secDetail")}</b>
                  <span className="ckc-chev">{advOpen ? t("cutterUi.collapse") : t("cutterUi.expand")}</span>
                </button>
                {!advOpen && (
                  <div className="ckc-adv-sum" onClick={() => setAdvOpen(true)}>
                    {/* 단위를 숫자마다 붙여 끝의 mm 만 다음 줄로 넘어가지 않게 한다 */}
                    {mode !== "stamp" && <>{t("cutter.knifeHeight")} <span className="ckc-nw">{toDisplayUnit(effectiveKnifeH)}{unitLabel}</span> · {t("cutter.knifeThickness")} <span className="ckc-nw">{toDisplayUnit(effectiveKnifeT)}{unitLabel}</span></>}
                    {mode === "both" && " · "}
                    {mode !== "cutter" && <>{t("cutter.stampTextHeight")} <span className="ckc-nw">{toDisplayUnit(effectiveStampTextH)}{unitLabel}</span></>}
                  </div>
                )}
                {advOpen && (
                  <div className="ckc-adv-body">
                    {/* 세부 치수는 3D 부품 색으로 나눈다 (2026-10-15 김현비) — 벽·베이스·도장·바닥 */}
                    {mode !== "stamp" && (<>
                      <div className="ckc-grp ckc-grp-part" style={{ "--part": PART_COLORS.cutterWall }}>
                        <div className="ckc-grp-t"><i style={{ background: PART_COLORS.cutterWall }} />{t("cutterUi.part.cutterWall")}</div>
                        <div className="ckc-fields">
                          <DimField label={t("cutterUi.dimHeight")} unitLabel={unitLabel} part="cutterWall" selectedPart={selectedPart} setSelectedPart={setSelectedPart}
                            range={`${toDisplayUnit(1)}~${toDisplayUnit(100)}`} step={unit === "in" ? "0.01" : "0.1"} min="1" max="100"
                            value={toDisplayUnit(effectiveKnifeH)} onChange={handleDimensionChange(setKnifeH, 1, 100)} onBlur={handleDimensionBlur(setKnifeH, 1, 100)} />
                          <DimField label={t("cutterUi.dimThickness")} unitLabel={unitLabel} part="cutterWall" selectedPart={selectedPart} setSelectedPart={setSelectedPart}
                            range={`${toDisplayUnit(1)}~${toDisplayUnit(10)}`} step={unit === "in" ? "0.01" : "0.1"} min="1" max="10"
                            value={toDisplayUnit(effectiveKnifeT)} onChange={handleDimensionChange(setKnifeT, 1, 10)} onBlur={handleDimensionBlur(setKnifeT, 1, 10)} />
                        </div>
                      </div>
                      <div className="ckc-grp ckc-grp-part" style={{ "--part": PART_COLORS.cutterBase }}>
                        <div className="ckc-grp-t"><i style={{ background: PART_COLORS.cutterBase }} />{t("cutterUi.part.cutterBase")}</div>
                        <div className="ckc-fields">
                          <DimField label={t("cutterUi.dimHeight")} unitLabel={unitLabel} part="cutterBase" selectedPart={selectedPart} setSelectedPart={setSelectedPart}
                            range={`${toDisplayUnit(1)}~${toDisplayUnit(100)}`} step={unit === "in" ? "0.01" : "0.1"} min="1" max="100"
                            value={toDisplayUnit(effectiveBaseH)} onChange={handleDimensionChange(setBaseH, 1, 100)} onBlur={handleDimensionBlur(setBaseH, 1, 100)} />
                          <DimField label={t("cutterUi.dimThickness")} unitLabel={unitLabel} part="cutterBase" selectedPart={selectedPart} setSelectedPart={setSelectedPart}
                            range={`${toDisplayUnit(1)}~${toDisplayUnit(10)}`} step={unit === "in" ? "0.01" : "0.1"} min="1" max="10"
                            value={toDisplayUnit(effectiveBaseT)} onChange={handleDimensionChange(setBaseT, 1, 10)} onBlur={handleDimensionBlur(setBaseT, 1, 10)} />
                        </div>
                      </div>
                    </>)}
                    {mode !== "cutter" && (<>
                      <div className="ckc-grp ckc-grp-part" style={{ "--part": PART_COLORS.stampFace }}>
                        <div className="ckc-grp-t"><i style={{ background: PART_COLORS.stampFace }} />{t("cutterUi.part.stampFace")}</div>
                        <div className="ckc-fields">
                          <DimField label={t("cutterUi.dimHeight")} unitLabel={unitLabel} part="stampFace" selectedPart={selectedPart} setSelectedPart={setSelectedPart}
                            range={`${toDisplayUnit(1)}~${toDisplayUnit(100)}`} step={unit === "in" ? "0.01" : "0.1"} min="1" max="100"
                            value={toDisplayUnit(effectiveStampTextH)} onChange={handleDimensionChange(setStampTextH, 1, 100)} onBlur={handleDimensionBlur(setStampTextH, 1, 100)} />
                        </div>
                      </div>
                      <div className="ckc-grp ckc-grp-part" style={{ "--part": PART_COLORS.stampBase }}>
                        <div className="ckc-grp-t"><i style={{ background: PART_COLORS.stampBase }} />{t("cutterUi.part.stampBase")}</div>
                        <div className="ckc-fields">
                          <DimField label={t("cutterUi.dimHeight")} unitLabel={unitLabel} part="stampBase" selectedPart={selectedPart} setSelectedPart={setSelectedPart}
                            range={`${toDisplayUnit(plugHeightRef.current + 1)}~${toDisplayUnit(100)}`} step={unit === "in" ? "0.01" : "0.1"} min={toDisplayUnit(plugHeightRef.current + 1)} max="100"
                            value={toDisplayUnit(effectiveStampBaseH)} onChange={handleDimensionChange(setStampBaseH, plugHeightRef.current + 1, 100)} onBlur={handleDimensionBlur(setStampBaseH, plugHeightRef.current + 1, 100)} />
                          <DimField label={t("cutter.stampFitGap")} unitLabel="mm" part="stampFitGap" selectedPart={selectedPart} setSelectedPart={setSelectedPart}
                            range={`${STAMP_FIT_GAP_MIN_MM}~${STAMP_FIT_GAP_MAX_MM}`} step="0.1" min={STAMP_FIT_GAP_MIN_MM} max={STAMP_FIT_GAP_MAX_MM}
                            value={stampFitGap}
                            onChange={(e) => {
                              const raw = e.target.value;
                              if (raw === "") { setStampFitGap(""); return; }
                              const v = Number(raw);
                              if (Number.isFinite(v)) setStampFitGap(Math.min(STAMP_FIT_GAP_MAX_MM, Math.max(STAMP_FIT_GAP_MIN_MM, v)));
                            }} />
                        </div>
                      </div>
                    </>)}
                    <div className="ckc-curve">
                      <div className="ckc-curve-h"><span>{t("cutter.curveSmoothness")}</span><b>{tempCurve}</b></div>
                      <input
                        className="ckc-range"
                        type="range"
                        min={CURVE_MIN}
                        max={CURVE_MAX}
                        step="1"
                        value={tempCurve}
                        style={{ "--p": `${curveProgress}%` }}
                        onChange={(e) => setTempCurve(Number(e.target.value))}
                        onMouseUp={() => setCurveSegments(tempCurve)}
                        onTouchEnd={() => setCurveSegments(tempCurve)}
                      />
                    </div>
                    {mode !== "cutter" && (
                      <label className="ckc-toggle">
                        <span>{t("cutter.stampOuterInclude")}</span>
                        <input type="checkbox" checked={stampOuterInclude} onChange={(e) => setStampOuterInclude(e.target.checked)} />
                        <i />
                      </label>
                    )}
                    <label className="ckc-toggle">
                      <span>{t("cutter.keepDimensionRatio")}</span>
                      <input type="checkbox" checked={keepDimensionRatio} onChange={(e) => setKeepDimensionRatio(e.target.checked)} />
                      <i />
                    </label>
                    <div className="ckc-mini-links">
                      <button type="button" className="ckc-pbtn save" title={t("cutterUi.presetHint")} onClick={saveDimensionPreset}>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z M17 21v-8H7v8 M7 3v5h8" /></svg>
                        {t("cutterUi.presetSaveBtn")}
                      </button>
                      <button type="button" className="ckc-pbtn" onClick={resetDimensionPreset}>
                        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M3 12a9 9 0 1 0 3-6.7L3 8 M3 3v5h5" /></svg>
                        {t("cutterUi.presetResetBtn")}
                      </button>
                    </div>
                    {presetMsg && <div className="ckc-preset-msg">✓ {presetMsg}</div>}
                  </div>
                )}
              </section>
            </div>
          </div>

          {/* 아래 고정 버튼 */}
          <div className="ckc-actions">
            {/* 비회원: 다운로드는 똑같이 되고, 보관·갤러리 올리기만 회원 전용 (2026-10-16 김현비) */}
            {!user && (
              <div className="ckc-guest">
                {t("cutter.guestSaveHint")}
                <button type="button" onClick={() => history.push("/login")}>{t("cutter.guestSaveLogin")}</button>
              </div>
            )}
            {user && (
              <label className="ckc-save">
                <input type="checkbox" checked={saveToMyPage} onChange={(e) => setSaveToMyPage(e.target.checked)} />
                <span className="ckc-cb" />
                {t("cutter.saveToMyPage")}
                {savedFileCount != null && <small>STL {savedFileCount} / {FILE_SAVE_LIMIT}</small>}
              </label>
            )}
            <button
              type="button"
              className="ckc-btn primary"
              onClick={exportSTL}
              disabled={!svgText || downloading || storageExceeded}
            >
              {downloading ? t("cutter.generatingShort") : t("cutter.downloadStl")}
              {!downloading && <small> · {t("cutterUi.freeUnlimited")}</small>}
            </button>
            <button type="button" className="ckc-btn line" disabled={!svgText} onClick={goOrderWithCurrentStl}>
              {t("cutterUi.orderThis")} →
            </button>
            {user && (
              <button type="button" className="ckc-btn line" disabled={!svgText} onClick={goGalleryWithCurrentStl}>
                {t("cutterUi.uploadToGallery")}
              </button>
            )}
          </div>
        </aside>

        {/* 리사이저 */}
        <div
          className="cc-resizer"
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
        />

        {/* 3D 뷰 */}
        <main
          className={`cc-view ${isDragging ? "drag-active" : ""}`}
          ref={mountRef}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {svgText && (
            <>
              <div className="ckc-vtool">
                {[["iso", t("cutterUi.view3d")], ["top", t("cutterUi.viewTop")], ["front", t("cutterUi.viewFront")]].map(([k, label]) => (
                  <button type="button" key={k} className={viewKind === k ? "on" : ""} onClick={() => setView(k)}>{label}</button>
                ))}
                <button type="button" className={autoRotate ? "on" : ""} onClick={toggleAutoRotate}>↻ {t("cutterUi.rotate")}</button>
              </div>
              <div className="ckc-legend">
                <b>{t("cutterUi.legendTitle")}</b>
                {LEGEND_PARTS.filter(([, , modes]) => modes.includes(mode)).map(([part, key]) => (
                  <button
                    type="button"
                    key={key}
                    className={selectedPart === part ? "on" : ""}
                    onClick={() => setSelectedPart(selectedPart === part ? null : part)}
                  >
                    <i style={{ background: PART_COLORS[key] }} />{t(`cutterUi.part.${key}`)}
                  </button>
                ))}
              </div>
              {modelDims && (
                <div className="ckc-dims">
                  <span><em>{t("cutterUi.width")}</em>{toDisplayUnit(modelDims.w)}</span>
                  <span><em>{t("cutterUi.depth")}</em>{toDisplayUnit(modelDims.d)}</span>
                  <span><em>{t("cutterUi.height")}</em>{toDisplayUnit(modelDims.h)} {unitLabel}</span>
                  <span className="note">
                    {mode !== "cutter" ? `${t("cutter.handleExcluded", "손잡이 제외")} · ` : ""}
                    {Math.max(modelDims.w, modelDims.d) <= BUILD_PLATE_SIZE_MM ? t("cutterUi.fitsPlate", { size: BUILD_PLATE_SIZE_MM }) : t("cutterUi.overPlate", { size: BUILD_PLATE_SIZE_MM })}
                  </span>
                </div>
              )}
              <div className="ckc-zoom">
                <button type="button" onClick={() => zoomBy(0.8)}>+</button>
                <button type="button" onClick={() => zoomBy(1.25)}>−</button>
                <button type="button" onClick={() => setView("iso")}>⤢</button>
              </div>
            </>
          )}

          {!svgText && !isRebuilding && (
            <div className="ckc-dropbig">
              <div className="ckc-dz-ic">↑</div>
              <h3>{t("cutterUi.dropTitle")}</h3>
              <p>{t("cutterUi.dropSub")}</p>
              <div className="ckc-dz-row">
                <label htmlFor="svgFile" className="ckc-btn primary dark">{t("cutter.selectSvg")}</label>
                <button type="button" className="ckc-btn line" onClick={() => history.push("/design")}>{t("cutterUi.noImageAi")}</button>
              </div>
              <div className="ckc-samples">
                <span>{t("cutterUi.tryExample")}</span>
                {EXAMPLES.map(([key, file]) => (
                  <button type="button" key={key} onClick={() => loadExample(file)} title={t(`cutterUi.example.${key}`)}>
                    <img src={`/home/${file}`} alt={t(`cutterUi.example.${key}`)} />
                  </button>
                ))}
              </div>
            </div>
          )}

          {isDragging && (
            <div className="drag-overlay">
              <div className="drag-text">{t("cutter.dropHere")}</div>
            </div>
          )}
          {isRebuilding && (
            <div className="cc-rebuild-overlay">
              <div className="cc-rebuild-icon">
                <span>⚙️</span>
              </div>
              <div className="cc-rebuild-text">{t(rebuildLabel === "generating" ? "cutter.generating3d" : "cutter.rebuilding")}</div>
              <div className="cc-rebuild-dots">
                <span /><span /><span />
              </div>
            </div>
          )}
        </main>
      </div>

      {/* SVG 가이드 팝업 */}
      {svgGuideOpen && (
        <div className="svg-guide-overlay" onClick={() => setSvgGuideOpen(false)}>
          <div className="svg-guide-modal" onClick={(e) => e.stopPropagation()}>
            <div className="svg-guide-header">
              <h3>{t("cutter.svgGuideTitle")}</h3>
              <button className="svg-guide-close" onClick={() => setSvgGuideOpen(false)}>&times;</button>
            </div>
            <div className="svg-guide-body">
              <div className="svg-guide-section">
                <p>{t("guide.cutter.tip2")}</p>
                <FillVsStrokeDiagram t={t} />
              </div>
              <div className="svg-guide-section">
                <p>{t("guide.cutter.tip3")}</p>
                <ClosedVsBrokenDiagram t={t} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}